import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Save, Trash2, Wand2, Info, AlertCircle, AlertTriangle, CheckCircle, Users, Settings, Plus, Merge, Edit, ChevronDown, ChevronUp, ChevronRight, FolderOpen, GripVertical, MessageCircle, Folder, BarChart3, Play, Clock, Eye, Download, X, Filter, HelpCircle, ClipboardList, RefreshCw, ShieldCheck, GitBranch, Square } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { pollVerificationJob, describeProgress } from '../../services/verificationJobPolling';
import toast from 'react-hot-toast';
import { toastService } from '../../services/toastService';
import Tooltip from './Tooltip';
import TerminologyTooltip from '../TerminologyTooltip';
import EnhancedStepChatModal from '../EnhancedStepChatModal';
import SOPContentRenderer from './SOPContentRenderer';
import SopEtaBanner from './SopEtaBanner';
import { getStepNumber } from '../../utils/stepNumbering';
import { deriveRegistrySectionNumbers, computeRevisionSectionNumber, isExcludedFromBrowserRender } from '../../utils/sopSectionNumbering';
import RichTextEditor, { RichTextEditorHandle } from '../RichTextEditor';
import AddSectionModal from '../AddSectionModal';
import ToolbarDropdown from '../ProcessMaps/ToolbarDropdown';
import HelpTooltip from '../HelpTooltip';
import AccuracyGateBadge from '../Verification/AccuracyGateBadge';
import { accuracyGateHeadline } from '../../utils/accuracyGate';
import Stage3VerificationBadge from '../Verification/Stage3VerificationBadge';
import VerificationPanel from '../Verification/VerificationPanel';
import ConfirmDialog from '../SecurityDocuments/ConfirmDialog';
import DownstreamStalenessBanner from './DownstreamStalenessBanner';
import { setNonOverlappingInterval } from './nonOverlappingInterval';

type StepObj = { step: string; sources?: any[]; inference?: string; is_expanded?: boolean };
function isStepObj(val: unknown): val is StepObj {
  return typeof val === 'object' && val !== null && 'step' in val;
}

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
}

interface ProcessStep {
  step: string;
  sources?: any[];
  inference?: string;
  is_expanded?: boolean;
  parent_step_index?: number;
  conversation_derived?: boolean;
  created_at?: string;
  section_id?: string;
}

interface ProcessSection {
  id: string;
  name: string;
  description?: string;
  color: string;
  order: number;
  step_count?: number;
}



const SOPGenerationTab: React.FC<{
  analysisResults: any;
  loadAnalysisResults: () => Promise<void>;
  isAnalyzing: boolean;
  analysisProgress: AnalysisProgress | null;
  globalComplianceScore: number | null;
  onRefresh?: () => void;
  approvedMapInfo?: { id: number; level: number; validation_status: string | null } | null;
  onGoToProcessMap?: () => void;
  onGoToTerminology?: () => void;
}> = ({ analysisResults, loadAnalysisResults, isAnalyzing, analysisProgress, globalComplianceScore, onRefresh, approvedMapInfo, onGoToProcessMap, onGoToTerminology }) => {

  // Early returns MUST come before any hooks to avoid violating Rules of Hooks
  if (!analysisResults) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="max-w-md mx-auto items-center justify-center flex flex-col">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Analysis Data</h3>
          <p className="text-gray-600 mb-4">Please run document analysis first.</p>
          <button
            onClick={onRefresh}
            className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  // Check 2: Documents uploaded but not analyzed
  const documents = analysisResults?.documents || [];
  const documentAnalyses = analysisResults?.document_analyses || [];

  if (documents.length > 0 && documentAnalyses.length === 0) {
    return (
      <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Documents Need Analysis</h3>
          <p className="text-gray-600 mb-4">
            You have {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded, but they haven't been analyzed yet.
          </p>
          <button
            onClick={onRefresh}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  const synthesis = analysisResults?.process_synthesis;

  // Check 3: Documents analyzed but no synthesis
  if (documentAnalyses.length > 0 && !synthesis) {
    return (
      <div className="text-center py-12 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Process Synthesis Required</h3>
          <p className="text-gray-600 mb-4">
            Documents have been analyzed, but process synthesis hasn't been completed yet. SOP generation requires process synthesis.
          </p>
          {/* <button
            onClick={onRefresh}
            className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Start Process Synthesis</span>
          </button> */}
        </div>
      </div>
    );
  }

  // Check 4: Synthesis complete but no SOP - let render continue to the !sop block below
  // (where generateBasicSOP and isBasicSOPGenerating are properly in scope after hooks)

  {/* Previous card implementation - Commented out to show loader during generation
  if (synthesis && !sop) {
    return (
      <div className="text-center py-12 bg-green-50 rounded-lg border-2 border-dashed border-green-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Ready for SOP Generation</h3>
          <p className="text-gray-600 mb-4">
            Process synthesis is complete. You can now generate your Standard Operating Procedure.
          </p>
          <button
            onClick={onRefresh}
            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <FileText className="w-4 h-4" />
            <span>Generate SOP</span>
          </button>
        </div>
      </div>
    );
  }
  */}

  // All hooks must be called after early returns to avoid Rules of Hooks violation
  const { currentProject } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [sopProgress, setSopProgress] = useState<{
    stage: string;
    progress: number;
    estimatedTime: number;
    startTime: number;
  } | null>(null);
  const [sopType, setSopType] = useState<'basic' | 'enhanced'>('basic');
  const [activeSOPTab, setActiveSOPTab] = useState<'basic' | 'enhanced' | 'process_map'>('enhanced'); // Default to Enhanced
  const [isBackgroundProcessingDetected, setIsBackgroundProcessingDetected] = useState(false);

  // Toggle view mode for transparency (Show All / Original Only / Additions Only)
  const [contentViewMode, setContentViewMode] = useState<'all' | 'original' | 'additions'>('all');
  const [showMarkers, setShowMarkers] = useState(true);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);
  const [isStage3Expanded, setIsStage3Expanded] = useState(false);
  const [dismissedStage3Issues, setDismissedStage3Issues] = useState<Set<string>>(new Set());
  // IR Verification Report inline-fix state — which finding's edit box is open, its draft text, and in-flight save
  const [openStage3FixKey, setOpenStage3FixKey] = useState<string | null>(null);
  const [stage3FixDraft, setStage3FixDraft] = useState('');
  const [isSavingStage3Fix, setIsSavingStage3Fix] = useState(false);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const backgroundPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBasicSOPGeneratingRef = useRef(false); // sync guard — prevents double-click race condition
  const isEnhancedGenerationPendingRef = useRef(false); // true from button click until polling starts

  // Editable steps state (new logic)
  const [isSaving, setIsSaving] = useState(false);
  const [saveQueued, setSaveQueued] = useState(false);
  const saveTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const sopPollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const [chatStepIdx, setChatStepIdx] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [isExportingSOP, setIsExportingSOP] = useState(false);
  const [isVerifyingSOP, setIsVerifyingSOP] = useState(false);
  const [isClearingSOP, setIsClearingSOP] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [pendingRegenerateType, setPendingRegenerateType] = useState<'basic' | 'enhanced' | null>(null);

  // Stakeholder and Systems state (matching Process Synthesis design)
  const [stakeholders, setStakeholders] = useState<{ [key: string]: any }>({});
  const [editingStakeholder, setEditingStakeholder] = useState<string | null>(null);
  const [editStakeholderValue, setEditStakeholderValue] = useState({ role: '', responsibility: '' });
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const [newStakeholder, setNewStakeholder] = useState({ role: '', responsibility: '' });
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([]);
  const [isStakeholdersCollapsed, setIsStakeholdersCollapsed] = useState(false);

  // Terminology definitions state
  const [terminologyDefinitions, setTerminologyDefinitions] = useState<any[]>([]);
  const [isLoadingTerminology, setIsLoadingTerminology] = useState(false);
  const [terminologySummary, setTerminologySummary] = useState<{ total: number; approved: number; pending: number } | null>(null);
  const [overrideTerminologyGate, setOverrideTerminologyGate] = useState(false);

  const [systems, setSystems] = useState<{ [key: string]: any }>({});
  const [editingSystem, setEditingSystem] = useState<string | null>(null);
  const [editSystemValue, setEditSystemValue] = useState({ system: '', purpose: '' });
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [newSystem, setNewSystem] = useState({ system: '', purpose: '' });
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [isSystemsCollapsed, setIsSystemsCollapsed] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [steps, setSteps] = useState<(string | ProcessStep)[]>([]);
  const [sections, setSections] = useState<ProcessSection[]>([]);
  const [sectionsMap, setSectionsMap] = useState<Map<string, string>>(new Map());
  const [sectionStepOrder, setSectionStepOrder] = useState<Map<string, number[]>>(new Map());
  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [draggedFromSection, setDraggedFromSection] = useState<string | null>(null);
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [unassignedStepsExpanded, setUnassignedStepsExpanded] = useState(false); // Changed to false - collapsed by default
  const [isSectionManagementCollapsed, setIsSectionManagementCollapsed] = useState(true); // Section Management collapsed by default
  const [isEnhancingInProgress, setIsEnhancingInProgress] = useState(false);
  // Timestamp the run started, so SopEtaBanner can show a live "time remaining."
  const [enhancedStartedAt, setEnhancedStartedAt] = useState<number | null>(null);
  const [isBasicSOPGenerating, setIsBasicSOPGenerating] = useState(false);
  const [isRegeneratingFromMap, setIsRegeneratingFromMap] = useState(false);
  const [processMapId, setProcessMapId] = useState<number | null>(null);
  const [isStatusMessageDismissed, setIsStatusMessageDismissed] = useState(false);
  const [hasRACIData, setHasRACIData] = useState<boolean>(false);

  // REQ-SOP-2.2: Inline editing state
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editedSections, setEditedSections] = useState<{[key: number]: {content: string, status: 'ai_generated' | 'user_modified' | 'user_approved'}}>({});
  const [editedSectionTitles, setEditedSectionTitles] = useState<{[key: number]: string}>({});
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  // Ref to the section's live editor. Only one section is in edit mode at a time, so a
  // single ref is enough. Save reads the current text straight from here, so an edit is
  // never lost to React-state timing (the root cause of edits reverting after reload).
  const sectionEditorRef = useRef<RichTextEditorHandle | null>(null);

  // REQ-SOP-2.3: Custom section addition state
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [insertAfterIndex, setInsertAfterIndex] = useState<number | null>(null);
  const sopRef = useRef<any>(null); // Initialize with null, will be updated in useEffect
  const isPollingCompletedRef = useRef(false);

  // Tool documentation comparison state
  const [availableToolPlatforms, setAvailableToolPlatforms] = useState<string[]>([]);
  const [showToolDocDropdown, setShowToolDocDropdown] = useState(false);
  const [selectedToolPlatforms, setSelectedToolPlatforms] = useState<string[]>([]);
  const [isComparingWithDocs, setIsComparingWithDocs] = useState(false);

  const loadAvailableToolPlatforms = useCallback(async () => {
    try {
      const res = await api.get('/api/workflow-tool-docs/platforms');
      if (res.data.success) setAvailableToolPlatforms(res.data.data);
    } catch (e) {
      console.error('Error loading tool platforms:', e);
    }
  }, []);

  // Check if RACI data exists for smart button behavior
  useEffect(() => {
    console.log('🔍 [RACI Check] useEffect triggered, currentProject:', currentProject?.id);

    const checkRACIData = async () => {
      if (!currentProject?.id) {
        console.log('⚠️ [RACI Check] No currentProject.id, skipping API call');
        return;
      }

      console.log(`🌐 [RACI Check] Calling API: /api/raci/${currentProject.id}`);
      try {
        const response = await api.get(`/api/raci/${currentProject.id}`);
        console.log('✅ [RACI Check] API Response:', response.data);

        if (response.data.success && response.data.data) {
          const { businessUnits, stakeholderRoles, assignments } = response.data.data;
          // Check if there are any assignments (either businessUnit or stakeholderRole)
          const hasData = (assignments?.businessUnit && Object.keys(assignments.businessUnit).length > 0) ||
                         (assignments?.stakeholderRole && Object.keys(assignments.stakeholderRole).length > 0);
          console.log(`📊 [RACI Check] Has RACI data: ${hasData}`);
          setHasRACIData(hasData);
        } else {
          console.log('📭 [RACI Check] No data in response');
          setHasRACIData(false);
        }
      } catch (error) {
        console.log('❌ [RACI Check] No RACI data exists yet:', error);
        setHasRACIData(false);
      }
    };

    checkRACIData();
  }, [currentProject?.id]);

  // Enhanced effect to load steps from Enhanced Process Synthesis Tab
  useEffect(() => {

    let stepsArr: (string | StepObj)[] | undefined;


    // Load sections from backend
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
    }

    if (synthesis?.process_steps && Array.isArray(synthesis.process_steps) && synthesis.process_steps.length > 0) {
      synthesis.process_steps.slice(0, 3).map((s: any, i: number) => ({
        index: i,
        step: typeof s === 'string' ? s.substring(0, 80) + '...' : s?.step?.substring(0, 80) + '...'
      }));
      stepsArr = synthesis.process_steps;
    }


    else if (synthesis?.synthesis_text) {
      console.log('📥 SOPGenerationTab: Falling back to synthesis_text parsing...');
      let synthesisData;
      try {
        synthesisData = typeof synthesis.synthesis_text === 'string'
          ? JSON.parse(synthesis.synthesis_text)
          : synthesis.synthesis_text;
      } catch (error) {
        console.error('SOPGenerationTab: Failed to parse synthesis_text:', error);
        synthesisData = synthesis.synthesis_text;
      }

      if (synthesisData?.end_to_end_process && Array.isArray(synthesisData.end_to_end_process)) {
        console.log('📥 SOPGenerationTab: Using end_to_end_process fallback:', synthesisData.end_to_end_process.length, 'steps');
        stepsArr = synthesisData.end_to_end_process;
      }
    }

    if (stepsArr && stepsArr.length > 0) {
      console.log('✅ SOPGenerationTab: Setting steps from:', {
        source: synthesis?.process_steps?.length > 0 ? 'synthesis.process_steps (Enhanced Tab)' : 'synthesis_text (Fallback)',
        count: stepsArr.length,
        firstStep: typeof stepsArr[0] === 'string' ? stepsArr[0].substring(0, 100) : stepsArr[0]?.step?.substring(0, 100),
        timestamp: new Date().toISOString()
      });

      // Update steps state
      setSteps(stepsArr);
    } else {
      setSteps([]);
    }
  }, [
    // Primary process steps dependencies  
    synthesis?.process_steps,
    synthesis?.synthesis_text,

    // Synthesis object dependencies
    synthesis,
    synthesis?.id,
    synthesis?.updated_at,

    // Parent analysis results dependencies to catch refresh triggers
    analysisResults,
    analysisResults?.process_synthesis,
    analysisResults?.updated_at,
    analysisResults?.timestamp,

    // Deep change detection for process steps array
    JSON.stringify(synthesis?.process_steps)
  ]);

  const addSection = useCallback(async () => {
    if (!newSectionName.trim() || !currentProject?.id) return;

    try {

      const newSection: ProcessSection = {
        id: `section_${Date.now()}`,
        name: newSectionName.trim(),
        description: newSectionDescription.trim(),
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        order: sections.length,
        step_count: 0
      };

      const updatedSections = [...sections, newSection];
      setSections(updatedSections);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(sectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      if (onRefresh) {
        console.log('🔄 Triggering global refresh after section add...');
        await onRefresh();
      }

      setNewSectionName('');
      setNewSectionDescription('');
      setShowAddSection(false);
      toast.success('Section added successfully');
    } catch (error) {
      console.error('❌ Error adding section:', error);
      toast.error('Failed to add section');

      // Revert local state on error
      setSections(sections);
    }
  }, [newSectionName, newSectionDescription, currentProject?.id, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  const deleteSection = useCallback(async (sectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🗑️ Deleting section:', sectionId);

      // Remove section
      const updatedSections = sections.filter(s => s.id !== sectionId);
      setSections(updatedSections);

      // Remove assignments from sectionsMap
      const newSectionsMap = new Map();
      sectionsMap.forEach((assignedSectionId, stepIndex) => {
        if (assignedSectionId !== sectionId) {
          newSectionsMap.set(stepIndex, assignedSectionId);
        }
      });
      setSectionsMap(newSectionsMap);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      if (onRefresh) {
        await onRefresh();
      }

      toast.success('Section deleted successfully');
    } catch (error) {
      toast.error('Failed to delete section');

      // Revert local state on error
      setSections(sections);
      setSectionsMap(sectionsMap);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type, onRefresh]);


  
  // Step management functions
  const deleteStep = async (stepIndex: number) => {
    if (!currentProject?.id) {
      toast.error('No project selected');
      return;
    }

    try {
      const updatedSteps = steps.filter((_, index) => index !== stepIndex);
      setSteps(updatedSteps);

      // Update sections map - shift indices for steps after deleted step
      const newSectionsMap = new Map();
      sectionsMap.forEach((sectionId, indexStr) => {
        const index = parseInt(indexStr);
        if (index < stepIndex) {
          newSectionsMap.set(indexStr, sectionId);
        } else if (index > stepIndex) {
          newSectionsMap.set((index - 1).toString(), sectionId);
        }
      });
      setSectionsMap(newSectionsMap);

      // CRITICAL: Save steps to backend
      await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
        steps: updatedSteps
      });

      // Save sections map to backend if we have sections
      if (sections.length > 0) {
        await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
          sections: sections,
          sectionsMap: Object.fromEntries(newSectionsMap),
          business_type: synthesis?.business_type || 'general'
        });
      }

      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 200);
      }

      toast.success('Step deleted and synchronized across tabs', {
        duration: 3000,
        icon: '🗑️'
      });

    } catch (error) {
      toast.error('Failed to delete step. Please try again.');

      // Revert local state on error
      setSteps(steps);
      setSectionsMap(sectionsMap);
    }
  };

  useEffect(() => {
    // Load sections from backend
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      console.log('📥 SOPGenerationTab: Loading sections from backend:', synthesis.sections.length, 'sections');
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
    }
  }, [
    synthesis?.sections,
    synthesis?.sections_map,
    synthesis?.section_step_order,
    JSON.stringify(synthesis?.sections),
    JSON.stringify(synthesis?.sections_map),
    JSON.stringify(synthesis?.section_step_order)
  ]);

  const moveStepToSection = useCallback(async (stepIndex: number, targetSectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🔄 Moving step', stepIndex, 'to section', targetSectionId);

      // Update local sectionsMap immediately
      const newSectionsMap = new Map(sectionsMap);
      newSectionsMap.set(stepIndex.toString(), targetSectionId);
      setSectionsMap(newSectionsMap);

      // IMPORTANT: Also update section_step_order to include this step at the end of target section
      const newSectionStepOrder = new Map(sectionStepOrder);
      const targetSectionOrder = newSectionStepOrder.get(targetSectionId) || [];

      // Remove step from any existing section orders
      newSectionStepOrder.forEach((order, sectionId) => {
        const filteredOrder = order.filter(idx => idx !== stepIndex);
        newSectionStepOrder.set(sectionId, filteredOrder);
      });

      // Add step to end of target section
      const updatedTargetOrder = [...targetSectionOrder.filter(idx => idx !== stepIndex), stepIndex];
      newSectionStepOrder.set(targetSectionId, updatedTargetOrder);
      setSectionStepOrder(newSectionStepOrder);

      // Save both maps to backend
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        section_step_order: Object.fromEntries(newSectionStepOrder),  // ✅ Include this!
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ Step moved and section order updated');

      if (onRefresh) {
        setTimeout(() => onRefresh(), 200);
      }

      toast.success('Step moved successfully', {
        duration: 2000,
        icon: '📁'
      });

    } catch (error) {
      toast.error('Failed to move step');

      // Revert local state on error
      setSectionsMap(sectionsMap);
      setSectionStepOrder(sectionStepOrder);
    }
  }, [currentProject?.id, sectionsMap, sectionStepOrder, sections, synthesis?.business_type, onRefresh]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Get unassigned steps
  const unassignedSteps = useMemo(() => {
    return steps
      .map((step, index) => ({ step, index }))
      .filter(({ index }) => !sectionsMap.has(index.toString()));
  }, [steps, sectionsMap]);

  // Group steps by section
  const stepsBySection = useMemo(() => {
    const grouped = new Map<string, Array<{ step: string | ProcessStep; index: number }>>();

    sections.forEach(section => {
      grouped.set(section.id, []);
    });

    // Get all steps that belong to sections
    const stepsInSections: Array<{ step: string | ProcessStep; index: number; sectionId: string }> = [];

    steps.forEach((step, index) => {
      const sectionId = sectionsMap.get(index.toString());
      if (sectionId && grouped.has(sectionId)) {
        stepsInSections.push({ step, index, sectionId });
      }
    });

    // Group steps by section and apply custom ordering
    stepsInSections.forEach(({ step, index, sectionId }) => {
      grouped.get(sectionId)!.push({ step, index });
    });

    // Apply custom ordering if it exists
    sections.forEach(section => {
      const sectionSteps = grouped.get(section.id) || [];
      const customOrder = sectionStepOrder.get(section.id);

      if (customOrder && customOrder.length > 0) {
        // Apply custom order for steps that are in the custom order
        const reorderedSteps: Array<{ step: string | ProcessStep; index: number }> = [];

        // First, add steps in their custom order
        customOrder.forEach(originalIndex => {
          const stepData = sectionSteps.find(s => s.index === originalIndex);
          if (stepData) {
            reorderedSteps.push(stepData);
          }
        });

        // Then, add any NEW steps that aren't in the custom order yet (append to end)
        sectionSteps.forEach(stepData => {
          if (!customOrder.includes(stepData.index)) {
            reorderedSteps.push(stepData);
          }
        });

        grouped.set(section.id, reorderedSteps);

        // Update the custom order to include the new steps
        if (reorderedSteps.length !== customOrder.length) {
          const newOrder = reorderedSteps.map(s => s.index);

          const newSectionStepOrder = new Map(sectionStepOrder);
          newSectionStepOrder.set(section.id, newOrder);
          setSectionStepOrder(newSectionStepOrder);
        }
      } else {
        sectionSteps.sort((a, b) => a.index - b.index);
        grouped.set(section.id, sectionSteps);

        // Create initial custom order based on current index order
        if (sectionSteps.length > 0) {
          const initialOrder = sectionSteps.map(s => s.index);
          const newSectionStepOrder = new Map(sectionStepOrder);
          newSectionStepOrder.set(section.id, initialOrder);
          setSectionStepOrder(newSectionStepOrder);
        }
      }
    });

    return grouped;
  }, [steps, sections, sectionsMap, sectionStepOrder]);

  useEffect(() => {
    const handleFocus = async () => {
      if (currentProject?.id && onRefresh) {

        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

    // Listen for window focus events
    window.addEventListener('focus', handleFocus);

    // Also listen for tab visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && currentProject?.id && onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentProject?.id, onRefresh]);

  useEffect(() => {
    if (currentProject?.id && onRefresh) {
      onRefresh();
    }
  }, []);

  const getProperStepNumber = (stepIndex: number) => {
    // Use the original step index to get the proper step number
    return getStepNumber(steps, stepIndex);
  };

  const handleSectionStepDragStart = (e: React.DragEvent, stepIndex: number, sectionId: string) => {

    setDraggedStepId(stepIndex);
    setDraggedFromSection(sectionId);

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'section-step',
      stepIndex: stepIndex,
      sectionId: sectionId,
      source: 'section'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionContainerDrop = async (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (dragData.type === 'main-flow-step' || dragData.type === 'section-step') {
        // This is moving a step TO a section (from main flow or from another section)
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section container drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };

  const handleSectionStepDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionStepDrop = async (e: React.DragEvent, targetStepIndex: number, targetSectionId: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent the container drop from also firing

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (dragData.type === 'section-step' && dragData.sectionId === targetSectionId) {
        // This is reordering within the same section
        if (dragData.stepIndex !== targetStepIndex) {
          await reorderStepsInSection(dragData.stepIndex, targetStepIndex, targetSectionId);
        }
      } else if (dragData.type === 'main-flow-step' ||
        (dragData.type === 'section-step' && dragData.sectionId !== targetSectionId)) {
        // This is moving a step TO this section
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section step drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };


  const reorderStepsInSection = useCallback(async (fromStepIndex: number, toStepIndex: number, sectionId: string) => {
    if (!currentProject?.id) return;



    try {
      // Get current section steps in their display order
      const currentSectionSteps = stepsBySection.get(sectionId) || [];

      const stepIndices = currentSectionSteps.map(s => s.index);

      // Find positions of the steps we're reordering
      const fromPosition = stepIndices.indexOf(fromStepIndex);
      const toPosition = stepIndices.indexOf(toStepIndex);

      if (fromPosition === -1 || toPosition === -1) {
        console.error('❌ Could not find step positions');
        return;
      }

      // Create new order by moving the step
      const newOrder = [...stepIndices];
      const [movedStepIndex] = newOrder.splice(fromPosition, 1);
      newOrder.splice(toPosition, 0, movedStepIndex);

      const newSectionStepOrder = new Map(sectionStepOrder);
      newSectionStepOrder.set(sectionId, newOrder);
      setSectionStepOrder(newSectionStepOrder);

      // Save to backend immediately
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(sectionsMap),
        business_type: synthesis?.business_type || 'general',
        section_step_order: Object.fromEntries(newSectionStepOrder)
      });

      // Trigger global refresh to sync all tabs
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 200);
      }

      toast.success('Steps reordered successfully', {
        duration: 2000,
        icon: '🔄'
      });

    } catch (error) {
      console.error('❌ Error reordering steps in section:', error);
      toast.error('Failed to reorder steps');

      // Revert local state on error
      setSectionStepOrder(sectionStepOrder);
    }
  }, [currentProject?.id, stepsBySection, sectionStepOrder, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  const handleMainFlowDragStart = (e: React.DragEvent, stepIndex: number) => {

    setDraggedStepId(stepIndex);
    setDraggedFromSection('main-flow');

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'main-flow-step',
      stepIndex: stepIndex,
      source: 'main-flow'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };


  useEffect(() => {
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);

    }
  }, [synthesis?.section_step_order]);


  const saveSectionOrder = useCallback(async (newSectionStepOrder: Map<string, number[]>) => {
    if (!currentProject?.id) return;

    try {
      const sectionOrderObj = Object.fromEntries(
        Array.from(newSectionStepOrder.entries()).map(([secId, order]) => [secId, order])
      );

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(sectionsMap),
        section_step_order: sectionOrderObj,
        business_type: synthesis?.business_type || 'general'
      });

    } catch (error) {
      console.error('❌ Error saving section order:', error);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type]);

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSaveSectionOrder = useCallback(
    debounce((newOrder: Map<string, number[]>) => {
      saveSectionOrder(newOrder);
    }, 500),
    [saveSectionOrder]
  );


  useEffect(() => {
    const synthesisText = synthesis?.synthesis_text;
    let synthesisData;
    try {
      synthesisData = typeof synthesisText === 'string' ?
        JSON.parse(synthesisText) : synthesisText;
    } catch {
      synthesisData = synthesis;
    }

    if (synthesisData?.stakeholders) {
      setStakeholders(synthesisData.stakeholders);
    }

    // IMPROVED: Handle different formats of systems_platforms
    if (synthesisData?.systems_platforms) {
      console.log('Loading systems from synthesis:', synthesisData.systems_platforms);

      let systemsData: { [key: string]: any } = {};

      // Handle object format (most common)
      if (typeof synthesisData.systems_platforms === 'object' && synthesisData.systems_platforms !== null && !Array.isArray(synthesisData.systems_platforms)) {
        // If it's already an object, ensure values are strings
        Object.entries(synthesisData.systems_platforms).forEach(([key, value]: [string, any]) => {
          if (typeof value === 'string') {
            systemsData[key] = value;
          } else if (typeof value === 'object' && value !== null) {
            // If value is an object, extract meaningful text
            systemsData[key] = value.purpose || value.description || value.name || JSON.stringify(value);
          } else {
            systemsData[key] = String(value);
          }
        });
      }
      // Handle array format
      else if (Array.isArray(synthesisData.systems_platforms)) {
        synthesisData.systems_platforms.forEach((system: any, index: number) => {
          if (typeof system === 'string') {
            systemsData[system] = 'System component';
          } else if (typeof system === 'object' && system !== null) {
            const key = system.name || system.system || `System ${index + 1}`;
            const value = system.purpose || system.description || 'System component';
            systemsData[key] = value;
          } else {
            systemsData[`System ${index + 1}`] = String(system);
          }
        });
      }
      // Handle string format (JSON string)
      else if (typeof synthesisData.systems_platforms === 'string') {
        try {
          const parsed = JSON.parse(synthesisData.systems_platforms);
          if (typeof parsed === 'object' && parsed !== null) {
            Object.entries(parsed).forEach(([key, value]: [string, any]) => {
              systemsData[key] = typeof value === 'string' ? value : String(value);
            });
          }
        } catch (e) {
          console.warn('Failed to parse systems_platforms string:', e);
          systemsData = { [synthesisData.systems_platforms]: 'System component' };
        }
      }

      setSystems(systemsData);
    }

    // Also try loading from direct synthesis fields as fallback
    if ((!synthesisData?.systems_platforms || Object.keys(synthesisData.systems_platforms || {}).length === 0) && synthesis?.systems_platforms) {
      console.log('Using fallback systems_platforms from direct synthesis field');
      let systemsData: { [key: string]: any } = {};

      if (typeof synthesis.systems_platforms === 'object' && synthesis.systems_platforms !== null) {
        Object.entries(synthesis.systems_platforms).forEach(([key, value]: [string, any]) => {
          systemsData[key] = typeof value === 'string' ? value : String(value);
        });
        setSystems(systemsData);
      }
    }
  }, [synthesis]);

  // Save steps to backend (debounced)
  const saveSteps = useCallback((newSteps: (string | StepObj)[]) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    setSaveQueued(true);
    saveTimeout.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        const projectId = currentProject?.id || synthesis?.project_id;
        if (!projectId) {
          console.error('No project ID available for saving steps');
          toast.error('Unable to save - no project selected');
          return;
        }
        console.log('Saving steps to backend:', { projectId, stepsCount: newSteps.length, steps: newSteps });
        const response = await api.put(`/api/analysis/process-steps/${projectId}`, { steps: newSteps });
        console.log('Steps saved successfully:', response.data);
        console.log('🔄 Refreshing parent data to sync across tabs...');

        // Refresh parent data so other tabs see the updated steps
        if (onRefresh) {
          onRefresh();
        }

        toast.success('Process steps updated successfully');
      } catch (err) {
        console.error('Failed to save steps:', err);
        toast.error('Failed to save process steps. Please try again.');
      } finally {
        setIsSaving(false);
        setSaveQueued(false);
      }
    }, 300);
  }, [currentProject?.id, synthesis, onRefresh]);

  // Stakeholder management functions
  const saveStakeholders = useCallback(async (newStakeholders: { [key: string]: any }) => {
    try {
      const projectId = currentProject?.id || synthesis?.project_id;
      if (!projectId) return;
      await api.put(`/api/analysis/stakeholders/${projectId}`, { stakeholders: newStakeholders });
      console.log('✅ Stakeholders saved successfully');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save stakeholders:', err);
      toast.error('Failed to save stakeholders');
    }
  }, [currentProject?.id, synthesis, onRefresh]);

  const startEditStakeholder = (role: string) => {
    setEditingStakeholder(role);
    setEditStakeholderValue({ role, responsibility: stakeholders[role] });
  };

  const saveStakeholderEdit = () => {
    if (!editingStakeholder) return;
    const updated = { ...stakeholders };
    if (editStakeholderValue.role !== editingStakeholder) {
      delete updated[editingStakeholder];
    }
    updated[editStakeholderValue.role] = editStakeholderValue.responsibility;
    setStakeholders(updated);
    setEditingStakeholder(null);
    saveStakeholders(updated);
  };

  const deleteStakeholder = (role: string) => {
    const updated = { ...stakeholders };
    delete updated[role];
    setStakeholders(updated);
    setSelectedStakeholders(selectedStakeholders.filter(r => r !== role));
    saveStakeholders(updated);
  };

  const addStakeholder = () => {
    if (!newStakeholder.role.trim()) return;
    const updated = { ...stakeholders, [newStakeholder.role]: newStakeholder.responsibility };
    setStakeholders(updated);
    setNewStakeholder({ role: '', responsibility: '' });
    setShowAddStakeholder(false);
    saveStakeholders(updated);
  };

  const mergeStakeholders = () => {
    if (selectedStakeholders.length < 2) return;
    const primaryRole = selectedStakeholders[0];
    const mergedResponsibility = selectedStakeholders.map(role => stakeholders[role]).join('; ');
    const updated = { ...stakeholders };
    updated[primaryRole] = mergedResponsibility;
    selectedStakeholders.slice(1).forEach(role => delete updated[role]);
    setStakeholders(updated);
    setSelectedStakeholders([]);
    saveStakeholders(updated);
  };

  // Systems management functions
  const saveSystems = useCallback(async (newSystems: { [key: string]: any }) => {
    try {
      const projectId = currentProject?.id || synthesis?.project_id;
      if (!projectId) return;
      await api.put(`/api/analysis/systems/${projectId}`, { systems: newSystems });
      console.log('✅ Systems saved successfully');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save systems:', err);
      toast.error('Failed to save systems');
    }
  }, [currentProject?.id, synthesis, onRefresh]);

  const startEditSystem = (system: string) => {
    setEditingSystem(system);
    setEditSystemValue({ system, purpose: systems[system] });
  };

  const saveSystemEdit = () => {
    if (!editingSystem) return;
    const updated = { ...systems };
    if (editSystemValue.system !== editingSystem) {
      delete updated[editingSystem];
    }
    updated[editSystemValue.system] = editSystemValue.purpose;
    setSystems(updated);
    setEditingSystem(null);
    saveSystems(updated);
  };

  const deleteSystem = (system: string) => {
    const updated = { ...systems };
    delete updated[system];
    setSystems(updated);
    setSelectedSystems(selectedSystems.filter(s => s !== system));
    saveSystems(updated);
  };

  const addSystem = () => {
    if (!newSystem.system.trim()) return;
    const updated = { ...systems, [newSystem.system]: newSystem.purpose };
    setSystems(updated);
    setNewSystem({ system: '', purpose: '' });
    setShowAddSystem(false);
    saveSystems(updated);
  };

  const mergeSystems = () => {
    if (selectedSystems.length < 2) return;
    const primarySystem = selectedSystems[0];
    const mergedPurpose = selectedSystems.map(system => systems[system]).join('; ');
    const updated = { ...systems };
    updated[primarySystem] = mergedPurpose;
    selectedSystems.slice(1).forEach(system => delete updated[system]);
    setSystems(updated);
    setSelectedSystems([]);
    saveSystems(updated);
  };

  // Get all SOPs and filter by active tab
  const allSOPs = useMemo(() => analysisResults?.sops || [], [analysisResults?.sops]);
  const basicSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'basic'), [allSOPs]);
  const enhancedSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'enhanced'), [allSOPs]);
  const processMapSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'process_map'), [allSOPs]);

  // Get current SOP based on active tab
  const sop = useMemo(() => {
    let currentSOP = null;
    if (activeSOPTab === 'basic' && basicSOPs.length > 0) {
      currentSOP = basicSOPs[0];
    } else if (activeSOPTab === 'enhanced' && enhancedSOPs.length > 0) {
      currentSOP = enhancedSOPs[0];
    } else if (activeSOPTab === 'process_map' && processMapSOPs.length > 0) {
      currentSOP = processMapSOPs[0];
    }
    // Backward compatibility: fallback to old sop field
    return currentSOP || analysisResults?.sop;
  }, [activeSOPTab, basicSOPs, enhancedSOPs, processMapSOPs, analysisResults?.sop]);

  const handleCompareWithToolDocs = useCallback(async () => {
    if (!sop?.id) { toast.error('No SOP found'); return; }
    if (!selectedToolPlatforms.length) { toast.error('Select at least one platform'); return; }
    setIsComparingWithDocs(true);
    setShowToolDocDropdown(false);
    try {
      const res = await api.post(`/api/workflow-tool-docs/compare-sop/${sop.id}`, {
        platforms: selectedToolPlatforms
      });
      // Bug 2 fix: only show success toast when the backend confirms
      // success. A 200 with success:false (or missing flag) means the
      // comparison ran but produced invalid output — show error instead.
      if (res.data?.success) {
        toast.success('Comparison complete! SOP updated with documentation markers.');
        await loadAnalysisResults();
      } else {
        toast.error(res.data?.message || 'Comparison produced invalid results. SOP was NOT modified.');
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.message || 'Comparison failed';
      // Legacy uploads with no extractable text still surface here. Offer a
      // one-click jump to Admin > Workflow Tools so the admin can re-upload
      // without losing their place in the SOP screen. New uploads are now
      // blocked at upload time (backend NO_EXTRACTABLE_TEXT 400), so this
      // path is mostly a safety net for docs ingested before that check.
      if (/extractable text/i.test(errorMsg)) {
        toast.error((t) => (
          <div className="flex flex-col gap-2">
            <span>{errorMsg}</span>
            <button
              className="self-start px-3 py-1 bg-electric-blue text-white rounded text-sm hover:bg-darker-blue transition-colors"
              onClick={() => { toast.dismiss(t.id); navigate('/app/admin'); }}
            >
              Go to Workflow Tools
            </button>
          </div>
        ), { duration: 10000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsComparingWithDocs(false);
    }
  }, [sop, selectedToolPlatforms, loadAnalysisResults, navigate]);

  // Update sopRef when sop changes
  useEffect(() => {
    sopRef.current = sop;
  }, [sop]);

  useEffect(() => {
    if (sop?.content) {
      let sopData;
      try {
        sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
        const method = sopData.compliance_assessment?.processing_method;
        const isAlreadyEnhanced = sopData.is_enhanced === true || sopData.is_enhanced === 'true';

        // If enhanced SOP is in state AND we are not actively polling for a new one,
        // generation is complete — clear all generating states.
        // Do NOT clear isEnhancingInProgress here if background polling is running
        // (sopPollIntervalRef.current != null), as that means a new enhanced SOP
        // is still being generated and the old enhanced SOP is just the stale value.
        if (isAlreadyEnhanced && !sopPollIntervalRef.current && !isEnhancedGenerationPendingRef.current) {
          setIsEnhancingInProgress(false);
          setIsBackgroundProcessingDetected(false);
          setIsBasicSOPGenerating(false);
        } else {
          // Only flag background processing for methods that indicate an enhanced SOP
          // is still being generated in the background.
          // 'basic_immediate' is NOT included — after our changes, basic SOPs are
          // standalone results, not placeholders for background enhanced generation.
          const isBackgroundProcessing = method && [
            'comprehensive_structured',
            'standard_analysis',
            'chunked_analysis'
          ].includes(method);
          setIsBackgroundProcessingDetected(!!isBackgroundProcessing);
          // Safety net: if any SOP loaded while basic generation was in progress, clear it
          setIsBasicSOPGenerating(false);
        }

        if (method === 'basic_immediate') {
          setSopType('basic');
        } else {
          setSopType('enhanced');
        }
      } catch {
        setSopType('basic');
      }
    }
  }, [sop]);

  // Auto-relink if step_linkages are in old format (missing process_step_title)
  useEffect(() => {
    if (!sop?.id || !sop?.content) return;
    try {
      const content = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      const linkages: any[] = content?.step_linkages || [];
      if (linkages.length === 0) return;
      const hasOldFormat = linkages.every((l: any) => !l.process_step_title && !l.process_step_number);
      if (!hasOldFormat) return;
      // Silently relink in background
      api.post(`/api/analysis/sops/${sop.id}/relink-steps`)
        .then(() => { if (onRefresh) onRefresh(); })
        .catch(() => {/* silent */});
    } catch {/* ignore parse errors */}
  }, [sop?.id]);

  // Fetch terminology definitions for SOP
  const fetchTerminologyDefinitions = useCallback(async () => {
    if (!currentProject?.id) return;

    setIsLoadingTerminology(true);
    try {
      console.log('🔍 Fetching terminology for project:', currentProject.id);
      const response = await api.get(`/api/terminology/${currentProject.id}/for-sop`);
      console.log('📥 Terminology API Response:', response.data);

      if (response.data.success) {
        const terms = response.data.data.terms || [];
        console.log('✅ Terminology terms loaded:', terms.length, terms);
        setTerminologyDefinitions(terms);
      } else {
        console.warn('⚠️ API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching terminology definitions:', error);
    } finally {
      setIsLoadingTerminology(false);
    }
  }, [currentProject?.id]);

  // Approval counts for the terminology gate. Kept separate from
  // fetchTerminologyDefinitions because that one only runs once a SOP exists,
  // and the gate has to be known before the first generation.
  const fetchTerminologySummary = useCallback(async () => {
    if (!currentProject?.id) return;
    try {
      const response = await api.get(`/api/terminology/${currentProject.id}/summary`);
      const summary = response.data?.data?.summary;
      setTerminologySummary(summary ? {
        total: Number(summary.total_terms) || 0,
        approved: Number(summary.approved_terms) || 0,
        pending: Number(summary.pending_review) || 0,
      } : null);
    } catch (error) {
      // Non-fatal: without counts the gate stays open rather than blocking
      // generation on a failed request.
      setTerminologySummary(null);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    if (currentProject?.id) fetchTerminologySummary();
  }, [currentProject?.id, sop?.id, fetchTerminologySummary]);

  useEffect(() => {
    setOverrideTerminologyGate(false);
  }, [currentProject?.id]);

  // "Pending Review" (requires_review) is the counter the Terminology screen
  // shows and the only one that is safe to gate on: merged terms stay in
  // total_terms with is_approved false, so approved < total can be permanent.
  const pendingTermCount = terminologySummary?.pending ?? 0;
  const terminologyIncomplete = pendingTermCount > 0;
  const terminologyGateActive = terminologyIncomplete && !overrideTerminologyGate;
  const pendingTermMessage = terminologySummary
    ? `${pendingTermCount} of ${terminologySummary.total} terms are not yet approved; Section 3.0 Definitions and Acronyms will only include the ${terminologySummary.approved} approved terms.`
    : '';

  // Delete terminology definition from SOP
  const deleteTerminologyDefinition = useCallback(async (termId: number) => {
    if (!currentProject?.id) return;

    try {
      const response = await api.delete(`/api/terminology/${currentProject.id}/sop-definition/${termId}`);
      if (response.data.success) {
        toast.success('Definition removed from SOP');
        // Refresh terminology list
        fetchTerminologyDefinitions();
      }
    } catch (error) {
      console.error('Error deleting terminology definition:', error);
      toast.error('Failed to remove definition');
    }
  }, [currentProject?.id, fetchTerminologyDefinitions]);

  // Load terminology definitions when component mounts or project changes
  useEffect(() => {
    if (currentProject?.id && sop) {
      fetchTerminologyDefinitions();
    }
  }, [currentProject?.id, sop, fetchTerminologyDefinitions]);
  // REQ-SOP-2.2: Handle section editing
  const handleEditSection = useCallback((index: number, currentContent: string, currentTitle?: string) => {
    setEditingSectionIndex(index);
    setEditedSections(prev => ({
      ...prev,
      [index]: {
        content: prev[index]?.content || currentContent,
        status: prev[index]?.status || 'ai_generated'
      }
    }));
    setEditedSectionTitles(prev => ({
      ...prev,
      [index]: prev[index] ?? (currentTitle || '')
    }));
  }, []);

  const handleSaveSection = useCallback(async (index: number) => {
    if (!currentProject || !sop) return;

    try {
      // Update section status to user_modified
      setEditedSections(prev => ({
        ...prev,
        [index]: { ...prev[index], status: 'user_modified' }
      }));

      // Get content and clean it before saving.
      // Prefer the LIVE editor text (via the imperative ref) over editedSections state:
      // the ref reflects the exact keystrokes on screen even if the debounced onChange
      // hasn't flushed to React state yet — this is what stops edits reverting on reload.
      // Fall back to state (e.g. the 30s auto-save firing after the editor unmounts).
      let contentToSave = sectionEditorRef.current?.getContent?.() ?? editedSections[index]?.content;
      if (contentToSave === undefined || contentToSave === null) return; // nothing to save

      // Apply the same cleanup logic that's used for display to ensure consistency
      if (typeof contentToSave === 'string') {
        // Strip any HTML tags that might have been left over
        if (contentToSave.includes('<p>') || contentToSave.includes('<br>')) {
          contentToSave = contentToSave
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>\s*<p>/gi, '\n\n')
            .replace(/<p>/gi, '')
            .replace(/<\/p>/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
        }

        // Convert emoji-based markers to proper bracket format
        contentToSave = contentToSave
          .replace(/📋\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
          .replace(/📋DOCUMENTED:/g, '[DOCUMENTED:')
          .replace(/🔍\*\*INFERRED:\*\*/g, '[INFERRED:')
          .replace(/🔍INFERRED:/g, '[INFERRED:')
          .replace(/💭\*\*ASSUMED:\*\*/g, '[ASSUMED:')
          .replace(/💭ASSUMED:/g, '[ASSUMED:')
          .replace(/🔍\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
          .replace(/🔍CONTROL POINT:/g, '[CONTROL POINT:')
          .replace(/⚠️\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
          .replace(/⚠️GAP IDENTIFIED:/g, '[GAP IDENTIFIED:')
          .replace(/📸\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
          .replace(/📸SCREENSHOT NEEDED:/g, '[SCREENSHOT NEEDED:')
          .replace(/📊\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
          .replace(/📊FLOWCHART NEEDED:/g, '[FLOWCHART NEEDED:')
          .replace(/⚡\*\*CONFLICT:\*\*/g, '[CONFLICT:')
          .replace(/⚡CONFLICT:/g, '[CONFLICT:')
          .replace(/📄\*\*SOURCE:\*\*/g, '[SOURCE:')
          .replace(/📄SOURCE:/g, '[SOURCE:')
          .replace(/💡\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:')
          .replace(/💡ASSUMPTION:/g, '[ASSUMPTION:');

        // Remove bold formatting from marker labels: [**DOCUMENTED:**text] -> [DOCUMENTED:text]
        contentToSave = contentToSave
          .replace(/\[\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
          .replace(/\[\*\*INFERRED:\*\*/g, '[INFERRED:')
          .replace(/\[\*\*ASSUMED:\*\*/g, '[ASSUMED:')
          .replace(/\[\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
          .replace(/\[\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
          .replace(/\[\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
          .replace(/\[\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
          .replace(/\[\*\*CONFLICT:\*\*/g, '[CONFLICT:')
          .replace(/\[\*\*SOURCE:\*\*/g, '[SOURCE:')
          .replace(/\[\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:');

        // Add closing brackets if missing
        contentToSave = contentToSave.replace(
          /(\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):)([^\]]+?)(?=(?:\.\s|\n|$|\s+\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):))/g,
          '$1$2]'
        );
      }

      // Save to backend with cleaned content + optional title change
      const sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      sopData.sections[index].content = contentToSave;
      sopData.sections[index].modification_status = 'user_modified';
      sopData.sections[index].last_edited_at = new Date().toISOString();
      const editedTitle = editedSectionTitles[index];
      if (editedTitle !== undefined && editedTitle.trim()) {
        sopData.sections[index].section_title = editedTitle.trim();
        sopData.sections[index].title = editedTitle.trim();
      }

      await api.put(`/api/analysis/sops/${sop.id}`, {
        content: JSON.stringify(sopData)
      });

      toastService.success('Section saved successfully');
      setEditingSectionIndex(null);

      // Clear the edited section from state after successful save
      setEditedSections(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      await loadAnalysisResults();
    } catch (error: any) {
      console.error('Save section error:', error);
      toastService.error('Failed to save section');
    }
  }, [currentProject, sop, editedSections, editedSectionTitles, loadAnalysisResults]);

  const handleCancelEdit = useCallback((index: number) => {
    setEditingSectionIndex(null);
    setEditedSections(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setEditedSectionTitles(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  }, []);

  // Issue-family key to its stage3_verification array field name — shared by both
  // the Apply Fix and Dismiss persistence paths below.
  const STAGE3_FAMILY_FIELD: Record<string, string> = {
    a: 'actor_issues',
    d: 'decision_issues',
    f: 'fabrication_issues',
    s: 'sequence_issues',
    u: 'duplicate_issues',
  };
  const STAGE3_FAMILY_WEIGHT: Record<string, number> = { a: 15, d: 10, f: 20, s: 10, u: 15 };

  // Remove one finding from stage3_verification's array (by its index within that
  // array), recompute overall_score/passed with the same weighted formula the
  // backend uses (enhancedSOPService.js), and return the updated object — does not
  // save by itself, callers persist it alongside whatever else they're saving so
  // Apply Fix and Dismiss each only make one PUT call.
  const removeStage3Issue = (verification: any, family: string, arrayIndex: number) => {
    const field = STAGE3_FAMILY_FIELD[family];
    const updated = { ...verification, [field]: [...(verification[field] || [])] };
    if (arrayIndex >= 0 && arrayIndex < updated[field].length) {
      updated[field].splice(arrayIndex, 1);
    }
    const remaining =
      (updated.actor_issues?.length || 0) +
      (updated.decision_issues?.length || 0) +
      (updated.fabrication_issues?.length || 0) +
      (updated.sequence_issues?.length || 0) +
      (updated.duplicate_issues?.length || 0);
    updated.passed = remaining === 0;
    updated.overall_score = Math.max(0, 100
      - (updated.actor_issues?.length || 0) * STAGE3_FAMILY_WEIGHT.a
      - (updated.decision_issues?.length || 0) * STAGE3_FAMILY_WEIGHT.d
      - (updated.fabrication_issues?.length || 0) * STAGE3_FAMILY_WEIGHT.f
      - (updated.sequence_issues?.length || 0) * STAGE3_FAMILY_WEIGHT.s
      - (updated.duplicate_issues?.length || 0) * STAGE3_FAMILY_WEIGHT.u
    );
    return updated;
  };

  // Apply an IR Verification Report finding's fix directly to its target section,
  // without navigating the user away from the report — replaces the flagged text
  // in place with the corrected text, removes the finding from stage3_verification
  // itself (not just the client-side dismissed set) so the Score and issue counts
  // persist across a reload, and saves both in one PUT.
  const applyStage3Fix = useCallback(async (
    sectionNumber: string,
    findText: string,
    replaceWith: string,
    resolvedIssue?: { family: string; arrayIndex: number }
  ) => {
    if (!currentProject || !sop) return;
    setIsSavingStage3Fix(true);
    // Optimistic instant-hide — see the matching comment in dismissStage3IssuePersisted.
    if (resolvedIssue) {
      setDismissedStage3Issues(prev => new Set(Array.from(prev).concat(`${resolvedIssue.family}${resolvedIssue.arrayIndex}`)));
    }
    try {
      const currentSopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      const idx = (currentSopData.sections || []).findIndex(
        (s: any) => (s.section_number || '').replace(/\.0$/, '') === sectionNumber.replace(/\.0$/, '')
      );
      if (idx === -1) {
        toastService.error(`Section ${sectionNumber} not found`);
        return;
      }
      const section = currentSopData.sections[idx];
      const originalContent: string = section.content || '';
      const updatedContent = findText && originalContent.includes(findText)
        ? originalContent.replace(findText, replaceWith)
        : `${originalContent}\n\n${replaceWith}`; // fallback: flagged text not found verbatim — append the correction

      // Intentionally do NOT set modification_status/last_edited_at here — IR-applied
      // fixes should only surface as a toast, not as a "Modified" badge / highlighted
      // border on the section card (that treatment is reserved for manual edits).
      currentSopData.sections[idx].content = updatedContent;

      if (resolvedIssue && currentSopData.stage3_verification) {
        currentSopData.stage3_verification = removeStage3Issue(
          currentSopData.stage3_verification, resolvedIssue.family, resolvedIssue.arrayIndex
        );
      }

      await api.put(`/api/analysis/sops/${sop.id}`, {
        content: JSON.stringify(currentSopData)
      });

      // The backend array just changed shape (one entry spliced out), so every
      // remaining entry's index shifted. dismissedStage3Issues keys (a0, a1, ...)
      // are positional and would now point at the wrong findings — clear it so the
      // next render's dismissed-filter starts clean against the freshly reloaded
      // stage3_verification, which is now the single source of truth for what's
      // still outstanding.
      if (resolvedIssue) setDismissedStage3Issues(new Set());

      toastService.success(`Section ${sectionNumber} updated`);
      await loadAnalysisResults();
    } catch (error: any) {
      console.error('Apply Stage 3 fix error:', error);
      toastService.error('Failed to save fix');
    } finally {
      setIsSavingStage3Fix(false);
    }
  }, [currentProject, sop, loadAnalysisResults]);

  // Dismiss a finding without editing SOP text — same persistence as Apply Fix
  // (removes it from stage3_verification and saves), so the Score and issue count
  // survive a reload instead of only living in client-side dismissedStage3Issues.
  const dismissStage3IssuePersisted = useCallback(async (family: string, arrayIndex: number) => {
    if (!currentProject || !sop) return;
    // Optimistic instant-hide: mark it dismissed right away so the finding
    // disappears immediately instead of waiting on the save + reload round-trip.
    setDismissedStage3Issues(prev => new Set(Array.from(prev).concat(`${family}${arrayIndex}`)));
    try {
      const currentSopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      if (!currentSopData.stage3_verification) return;
      currentSopData.stage3_verification = removeStage3Issue(currentSopData.stage3_verification, family, arrayIndex);

      await api.put(`/api/analysis/sops/${sop.id}`, {
        content: JSON.stringify(currentSopData)
      });

      // The backend array just changed shape (one entry spliced out), so every
      // remaining entry's index shifted. The optimistic key set above is now
      // stale — clear it once stage3_verification (now the single source of
      // truth for what's still outstanding) has been reloaded fresh.
      setDismissedStage3Issues(new Set());

      await loadAnalysisResults();
    } catch (error: any) {
      console.error('Dismiss Stage 3 issue error:', error);
      toastService.error('Failed to dismiss finding');
    }
  }, [currentProject, sop, loadAnalysisResults]);

  const handleContentChange = useCallback((index: number, newContent: string) => {
    setEditedSections(prev => ({
      ...prev,
      [index]: { ...prev[index], content: newContent }
    }));

    // Auto-save after 30 seconds
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const timer = setTimeout(() => {
      handleSaveSection(index);
    }, 30000); // 30 seconds

    setAutoSaveTimer(timer);
  }, [autoSaveTimer, handleSaveSection]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  // Auto-generate Key Definitions section from approved terminology
  // REQ-SOP-2.3: Handle custom section addition
  const handleAddSection = useCallback(async (newSection: { title: string; content: string; template?: string }) => {
    if (!currentProject || !sop) return;

    try {
      const sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;

      // Determine insert position
      const insertIndex = insertAfterIndex !== null ? insertAfterIndex + 1 : sopData.sections.length;

      // Create new section with auto-numbered section_number
      const newSectionNumber = `${insertIndex + 1}.0`;
      const customSection = {
        section_number: newSectionNumber,
        section_title: newSection.title,
        content: newSection.content,
        modification_status: 'user_modified',
        is_custom: true,
        created_at: new Date().toISOString()
      };

      // Insert section at position
      sopData.sections.splice(insertIndex, 0, customSection);

      // Renumber subsequent sections
      for (let i = insertIndex + 1; i < sopData.sections.length; i++) {
        const oldNumber = sopData.sections[i].section_number;
        const newNumber = `${i + 1}.0`;
        sopData.sections[i].section_number = newNumber;

        // Update any subsection numbers if they exist
        if (oldNumber && oldNumber.includes('.')) {
          const parts = oldNumber.split('.');
          if (parts.length > 1) {
            sopData.sections[i].section_number = `${i + 1}.${parts.slice(1).join('.')}`;
          }
        }
      }

      // Save to backend
      await api.post(`/api/analysis/sops/${sop.id}/sections`, {
        content: JSON.stringify(sopData)
      });

      toastService.success('Custom section added successfully');
      await loadAnalysisResults();

      setIsAddSectionModalOpen(false);
      setInsertAfterIndex(null);
    } catch (error: any) {
      console.error('Add section error:', error);
      toastService.error('Failed to add section');
    }
  }, [currentProject, sop, insertAfterIndex, loadAnalysisResults]);

  const openAddSectionModal = useCallback((afterIndex: number | null = null) => {
    setInsertAfterIndex(afterIndex);
    setIsAddSectionModalOpen(true);
  }, []);

  const clearSOP = useCallback(async () => {
    const projectId = currentProject?.id || analysisResults?.project_id;
    if (!projectId) return;
    setIsClearingSOP(true);
    try {
      await api.delete(`/api/documents/sop/project/${projectId}`);
      await loadAnalysisResults();
      toastService.success('SOPs cleared successfully');
    } catch (err: any) {
      toastService.error(err?.response?.data?.error || 'Failed to clear SOPs');
    } finally {
      setIsClearingSOP(false);
      setShowClearConfirm(false);
    }
  }, [currentProject, analysisResults, loadAnalysisResults]);

  const generateBasicSOP = useCallback(async () => {
    if (!currentProject) {
      toastService.error('Please select a project first');
      return;
    }
    // Sync ref guard — state update is async, ref check is immediate
    if (isBasicSOPGeneratingRef.current) return;
    isBasicSOPGeneratingRef.current = true;
    setIsBasicSOPGenerating(true);
    let keepLoading = false;
    try {
      toastService.info('Generating SOP — this may take 1–3 minutes...');
      const response = await api.post(`/api/analysis/generate-basic-sop/${currentProject.id}`);

      if (response.data.sop_id) {
        toastService.success('SOP generated successfully!');
        await loadAnalysisResults();
      }
    } catch (error: any) {
      console.error('Basic SOP generation error:', error);
      if (error.response?.status === 429) {
        // Backend already generating — keep loading spinner, poll until SOP appears
        keepLoading = true;
        let pollCount = 0;
        const maxPolls = 60; // 10 min at 10s intervals
        if (backgroundPollingRef.current) clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = setNonOverlappingInterval(async () => {
          pollCount++;
          try {
            const res = await api.get(`/api/analysis/results/${currentProject.id}`, {
              timeout: 20000
            });
            const latestSop = res.data.sop;
            const isNew = latestSop && (!sopRef.current ||
              latestSop.id !== sopRef.current.id ||
              latestSop.version > (sopRef.current.version || 0));
            if (isNew || pollCount >= maxPolls) {
              clearInterval(backgroundPollingRef.current!);
              backgroundPollingRef.current = null;
              isBasicSOPGeneratingRef.current = false;
              setIsBasicSOPGenerating(false);
              if (isNew) {
                toastService.success('SOP generated successfully!');
                await loadAnalysisResults();
              }
            }
          } catch { /* ignore transient poll errors */ }
        }, 10000);
      } else {
        toastService.error(error.response?.data?.error || 'Failed to generate SOP');
      }
    } finally {
      if (!keepLoading) {
        isBasicSOPGeneratingRef.current = false;
        setIsBasicSOPGenerating(false);
      }
    }
  }, [currentProject, loadAnalysisResults]);

  const generateEnhancedSOP = useCallback(async () => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }
    setIsEnhancingInProgress(true);
    setEnhancedStartedAt(Date.now());
    isEnhancedGenerationPendingRef.current = true;
    setIsStatusMessageDismissed(false); // Reset dismissed state for new generation
    // Clear any existing intervals
    if (sopPollIntervalRef.current) {
      clearInterval(sopPollIntervalRef.current);
      sopPollIntervalRef.current = null;
    }
    isPollingCompletedRef.current = false;

    try {
      // Start the Enhanced SOP generation
      const response = await api.post(`/api/analysis/generate-enhanced-sop/${currentProject.id}`);
      console.log('✅ API Response:', response.data);

      // Refresh immediately — basic SOP may have been created server-side as placeholder
      await loadAnalysisResults();

      // Set background processing state instead of upgrading state
      setIsBackgroundProcessingDetected(true);

      // Show initial toast
      toast.success('🚀 Enhanced SOP generation started! Processing in background...', {
        duration: 4000
      });

      // Start simple background polling
      isEnhancedGenerationPendingRef.current = false; // polling started, sentinel no longer needed
      let pollCount = 0;
      const maxPolls = 180; // 15 minutes at 5-second intervals

      sopPollIntervalRef.current = setNonOverlappingInterval(async () => {
        pollCount++;
        console.log(`🔍 Background polling ${pollCount}/${maxPolls}`);

        try {
          // Override the client default (5 minutes, sized for AI generation
          // calls). A status poll that has not answered within 20s is not worth
          // holding a connection for — the next tick will ask again.
          const resultsResponse = await api.get(`/api/analysis/results/${currentProject.id}`, {
            timeout: 20000
          });
          const latestSop = resultsResponse.data.sop;

          if (latestSop && latestSop.content) {
            // Check for backend failure marker first
            try {
              const sopData = typeof latestSop.content === 'string' ?
                JSON.parse(latestSop.content) : latestSop.content;
              if (sopData._enhanced_generation_failed) {
                console.log('❌ Enhanced SOP backend failure detected');
                clearInterval(sopPollIntervalRef.current!);
                sopPollIntervalRef.current = null;
                setIsEnhancingInProgress(false);
                setIsBackgroundProcessingDetected(false);
                toast.error('Enhanced SOP generation failed. Basic SOP is available.');
                return;
              }
            } catch (_) {
              // ignore parse error, continue polling
            }

            // Check if this is a newer SOP
            const isNewOrUpdated = !sopRef.current ||
              latestSop.id !== sopRef.current.id ||
              latestSop.updated_at !== sopRef.current.updated_at ||
              latestSop.version > (sopRef.current.version || 1);

            console.log('📊 Background check:', {
              isNewOrUpdated,
              latestVersion: latestSop.version,
              currentVersion: sopRef.current?.version,
              latestUpdated: latestSop.updated_at,
              currentUpdated: sopRef.current?.updated_at
            });

            if (isNewOrUpdated) {
              let isEnhanced = false;

              try {
                const sopData = typeof latestSop.content === 'string' ?
                  JSON.parse(latestSop.content) : latestSop.content;

                // Primary: explicit flag set by backend on successful enhanced save
                if (sopData.is_enhanced === true || sopData.is_enhanced === 'true') {
                  isEnhanced = true;
                } else {
                  // Fallback heuristic for older records without the flag (require 8+ sections to avoid false-positives with basic SOPs)
                  const hasMultipleSections = sopData.sections && sopData.sections.length >= 8;
                  const hasSubstantialContent = JSON.stringify(sopData).length > 3000;
                  const hasEnhancedMarkers = JSON.stringify(sopData).includes('CONTROL POINT') ||
                    JSON.stringify(sopData).includes('GAP IDENTIFIED');
                  isEnhanced = hasMultipleSections && (hasSubstantialContent || hasEnhancedMarkers);
                }

                console.log('📋 Enhancement check:', {
                  is_enhanced_flag: (typeof latestSop.content === 'string' ? JSON.parse(latestSop.content) : latestSop.content).is_enhanced,
                  isEnhanced
                });

              } catch (parseError) {
                // If parsing fails but we have substantial content, consider it enhanced
                const hasContent = typeof latestSop.content === 'string' ?
                  latestSop.content.length > 3000 :
                  JSON.stringify(latestSop.content).length > 3000;
                isEnhanced = hasContent;
              }

              if (isEnhanced) {
                if (isPollingCompletedRef.current) return;
                isPollingCompletedRef.current = true;

                console.log('🎉 Enhanced SOP completed in background!');

                // Clear polling first
                if (sopPollIntervalRef.current) {
                  clearInterval(sopPollIntervalRef.current);
                  sopPollIntervalRef.current = null;
                }

                // Show success message and reload
                toast.success('🎉 Enhanced SOP generation completed!');
                await loadAnalysisResults();

                // Clear generating states AFTER reload so banner doesn't flash back
                setIsEnhancingInProgress(false);
                setIsBackgroundProcessingDetected(false);
                return;
              }
            }
          }

          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            console.log('⏱️ Background polling timeout');

            if (sopPollIntervalRef.current) {
              clearInterval(sopPollIntervalRef.current);
              sopPollIntervalRef.current = null;
            }

            setIsEnhancingInProgress(false);
            setIsBackgroundProcessingDetected(false);
            toast('⏱️ Enhanced SOP generation is taking longer than expected. It may still be processing.', {
              duration: 6000
            });
          }

        } catch (pollError) {
          console.error('❌ Background polling error:', pollError);
        }
      }, 5000); // Poll every 5 seconds

    } catch (error) {
      console.error('❌ Enhanced SOP generation failed:', error);
      isEnhancedGenerationPendingRef.current = false;
      setIsEnhancingInProgress(false);
      setIsBackgroundProcessingDetected(false);
      toast.error('Enhanced SOP generation failed. Please try again.');
    }
  }, [currentProject, loadAnalysisResults]);

  const cancelEnhancedSOP = useCallback(async () => {
    if (!currentProject) return;
    try {
      await api.post(`/api/analysis/cancel-enhanced-sop/${currentProject.id}`);
      if (sopPollIntervalRef.current) {
        clearInterval(sopPollIntervalRef.current);
        sopPollIntervalRef.current = null;
      }
      isEnhancedGenerationPendingRef.current = false;
      setIsEnhancingInProgress(false);
      setIsBackgroundProcessingDetected(false);
      toast.success('Enhanced SOP generation stopped. The current AI call may finish, then no further calls run.');
    } catch (error) {
      console.error('Failed to cancel Enhanced SOP generation:', error);
      toast.error('Could not stop Enhanced SOP generation. Please try again.');
    }
  }, [currentProject]);

  // Fetch process map ID on component mount
  useEffect(() => {
    const fetchProcessMapId = async () => {
      if (!currentProject?.id) return;

      try {
        const response = await api.get(`/api/analysis/projects/${currentProject.id}/process-maps`);
        if (response.data?.process_maps && response.data.process_maps.length > 0) {
          // Prefer L3 map for SOP regeneration — SOP is linked to L3 operational steps
          const l3Map = response.data.process_maps.find((m: any) => m.level === 3);
          const map = l3Map || response.data.process_maps[0];
          setProcessMapId(map.id);
        }
      } catch (error) {
        console.error('Error fetching process map ID:', error);
      }
    };

    fetchProcessMapId();
  }, [currentProject?.id]);

  // Regenerate SOP from Process Map (no AI)
  const regenerateFromProcessMap = useCallback(async () => {
    if (!processMapId) {
      toastService.error('No process map found for this project');
      return;
    }

    setIsRegeneratingFromMap(true);
    try {
      toastService.info('🗺️ Regenerating SOP from process map...');

      const response = await api.post('/api/analysis/sops/regenerate-from-map', {
        processMapId: processMapId,
        preserveVerified: true // Preserve existing content, only update step titles
      });

      if (response.data.success) {
        const { sop, mappings, message } = response.data;

        toastService.success(`✅ SOP sections updated from Process Map`);

        console.log('📊 Regeneration result:', { sopId: sop.id, mappingsCreated: mappings.created });

        // Reload analysis results from DB — does NOT re-generate Enhanced SOP
        await loadAnalysisResults();
      }
    } catch (error: any) {
      console.error('Regenerate from process map error:', error);
      toastService.error(error.response?.data?.error || 'Failed to regenerate SOP from process map');
    } finally {
      setIsRegeneratingFromMap(false);
    }
  }, [processMapId, loadAnalysisResults]);

  useEffect(() => {
    return () => {
      if (sopPollIntervalRef.current) {
        clearInterval(sopPollIntervalRef.current);
        sopPollIntervalRef.current = null;
      }
      // Reset button state on component unmount
      setIsEnhancingInProgress(false);
    };
  }, []);


  useEffect(() => {
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      console.log('📥 ProcessMaps: Loading sections from backend:', synthesis.sections.length, 'sections');
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      console.log('📥 ProcessMaps: Loading section step order from backend');
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
      console.log('📥 ProcessMaps: Loaded section step order from backend:', Object.fromEntries(orderMap));
    }
  }, [
    synthesis?.sections,
    synthesis?.sections_map,
    synthesis?.section_step_order,
    JSON.stringify(synthesis?.sections),
    JSON.stringify(synthesis?.sections_map),
    JSON.stringify(synthesis?.section_step_order)
  ]);

  // ADD STEPS LOADING useEffect
  useEffect(() => {
    console.log('📥 ProcessMaps: Loading steps from Enhanced Process Synthesis Tab...', {
      'synthesis.process_steps exists': !!synthesis?.process_steps,
      'synthesis.process_steps length': synthesis?.process_steps?.length || 0
    });

    let stepsArr: any[] = [];

    // PRIORITY 1: Use process_steps from backend (Enhanced Tab data)
    if (synthesis?.process_steps && Array.isArray(synthesis.process_steps) && synthesis.process_steps.length > 0) {
      console.log('📥 ProcessMaps: Loading from synthesis.process_steps (Enhanced Tab Updates):', synthesis.process_steps.length, 'steps');
      stepsArr = synthesis.process_steps;
    }
    // PRIORITY 2: Fallback to synthesis_text
    else if (synthesis?.synthesis_text) {
      console.log('📥 ProcessMaps: Falling back to synthesis_text parsing...');
      let synthesisData;
      try {
        synthesisData = typeof synthesis.synthesis_text === 'string'
          ? JSON.parse(synthesis.synthesis_text)
          : synthesis.synthesis_text;
      } catch (error) {
        console.error('ProcessMaps: Failed to parse synthesis_text:', error);
        synthesisData = synthesis.synthesis_text;
      }

      if (synthesisData?.end_to_end_process && Array.isArray(synthesisData.end_to_end_process)) {
        console.log('📥 ProcessMaps: Using end_to_end_process fallback:', synthesisData.end_to_end_process.length, 'steps');
        stepsArr = synthesisData.end_to_end_process;
      }
    }

    if (stepsArr.length > 0) {
      console.log('✅ ProcessMaps: Setting steps from Enhanced Tab sync:', stepsArr.length, 'steps');
      setSteps(stepsArr);
    } else {
      console.log('❌ ProcessMaps: No steps found - clearing steps array');
      setSteps([]);
    }
  }, [
    synthesis?.process_steps,
    synthesis?.synthesis_text,
    synthesis?.id,
    synthesis?.updated_at,
    JSON.stringify(synthesis?.process_steps)
  ]);

  // Background processing polling effect — only for page-reload scenario, not active generation
  useEffect(() => {
    if (isBackgroundProcessingDetected && currentProject && !isUpgrading && !isEnhancingInProgress) {
      let pollAttempts = 0;
      const maxPollAttempts = 120; // 10 minutes at 5-second intervals

      toast('🔄 Background Enhanced SOP generation detected! Monitoring for completion...', {
        icon: 'ℹ️',
        duration: 5000
      });

      backgroundPollingRef.current = setNonOverlappingInterval(async () => {
        pollAttempts++;

        try {
          const resultsResponse = await api.get(`/api/analysis/results/${currentProject.id}`, {
            timeout: 20000
          });
          const currentSop = resultsResponse.data.sop;

          if (currentSop && currentSop.content) {
            let sopData;
            try {
              sopData = typeof currentSop.content === 'string' ? JSON.parse(currentSop.content) : currentSop.content;
              const method = sopData.compliance_assessment?.processing_method;

              // Check if enhanced processing is complete
              const isComplete = sopData.is_enhanced === true || sopData.is_enhanced === 'true' || (method && [
                'comprehensive_enhanced',
                'hybrid_enhanced',
                'single_prompt_enhanced'
              ].includes(method));

              if (isComplete) {
                // Enhanced SOP is ready!
                if (backgroundPollingRef.current) {
                  clearInterval(backgroundPollingRef.current);
                  backgroundPollingRef.current = null;
                }

                setIsBackgroundProcessingDetected(false);
                toast.success('🎉 Enhanced SOP generation completed! Refreshing results...');
                await loadAnalysisResults();
                return;
              }
            } catch (e) {
              console.warn('Error parsing SOP during background polling:', e);
            }
          }

        } catch (pollError) {
          console.warn('Background polling error (will continue):', pollError);
        }

        // Stop polling after max attempts
        if (pollAttempts >= maxPollAttempts) {
          if (backgroundPollingRef.current) {
            clearInterval(backgroundPollingRef.current);
            backgroundPollingRef.current = null;
          }
          setIsBackgroundProcessingDetected(false);
          toast('⚠️ Background processing monitoring stopped after 10 minutes. Please refresh manually to check for updates.', {
            icon: '⚠️',
            duration: 8000
          });
        }
      }, 5000);
    }

    return () => {
      if (backgroundPollingRef.current) {
        clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = null;
      }
    };
  }, [isBackgroundProcessingDetected, currentProject, isUpgrading, isEnhancingInProgress, loadAnalysisResults]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (sopProgress) {
        setSopProgress(null);
      }
      // Clear any running intervals
      if (sopPollIntervalRef.current) {
        clearInterval(sopPollIntervalRef.current);
        sopPollIntervalRef.current = null;
      }
      if (backgroundPollingRef.current) {
        clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = null;
      }
    };
  }, [sopProgress]);

  // Show a loader during the main analysis process
  // const analysisHasRun = analysisProgress && (analysisProgress.completed > 0 || analysisProgress.failed > 0);
  // const isWaitingForSopAfterAnalysis = !isAnalyzing && analysisHasRun && !sop;
  // const isAnalysisInProgress = isAnalyzing || (analysisProgress && analysisProgress.analyzing > 0);

  // const shouldShowLoader = isAnalysisInProgress || isWaitingForSopAfterAnalysis || (analysisProgress && analysisProgress.total > 0 && !sop);


  
  // if (shouldShowLoader) {
  //   let message = 'Loading SOP...';
  //   let subtitle = '';

  //   if (isAnalyzing || isAnalysisInProgress) {
  //     message = 'Analysis in Progress';
  //     subtitle = 'The SOP will be generated automatically upon completion.';
  //   } else if (isWaitingForSopAfterAnalysis) {
  //     message = 'Generating SOP';
  //     subtitle = 'Finalizing analysis results and creating your Standard Operating Procedure...';
  //   } else if (analysisProgress && analysisProgress.total > 0) {
  //     message = 'Processing Documents';
  //     subtitle = `${analysisProgress.completed}/${analysisProgress.total} documents processed. SOP generation will begin shortly.`;
  //   }

  //   return (
  //     <div className="flex flex-col items-center justify-center py-16">

  //       {isBackgroundProcessingDetected && !isUpgrading && (
  //         <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
  //           <div className="flex items-center justify-between">
  //             <div className="flex items-center space-x-2">
  //               <div className="flex space-x-1">
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
  //               </div>
  //               <span className="text-blue-800 font-medium">Enhanced SOP generation in progress...</span>
  //             </div>
  //             <span className="text-xs text-blue-600">This may take 3-8 minutes</span>
  //           </div>
  //         </div>
  //       )}
  //       {/* Enhanced Loading Animation */}
  //       <div className="relative mb-6">
  //         <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
  //         <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
  //         <div className="absolute inset-0 flex items-center justify-center">
  //           <div className="w-6 h-6 bg-electric-blue rounded-full animate-pulse"></div>
  //         </div>
  //       </div>

  //       <h3 className="text-xl font-semibold text-gray-800 mb-2">{message}</h3>
  //       <p className="text-gray-600 text-center max-w-md">{subtitle}</p>

  //       {/* Progress indicator if analysis progress is available */}
  //       {analysisProgress && analysisProgress.total > 0 && (
  //         <div className="w-64 mt-6">
  //           <div className="bg-gray-200 rounded-full h-2">
  //             <div
  //               className="bg-electric-blue h-2 rounded-full transition-all duration-300"
  //               style={{ width: `${(analysisProgress.completed / analysisProgress.total) * 100}%` }}
  //             ></div>
  //           </div>
  //           <div className="flex justify-between text-xs text-gray-500 mt-1">
  //             <span>Processing...</span>
  //             <span>{Math.round((analysisProgress.completed / analysisProgress.total) * 100)}%</span>
  //           </div>
  //         </div>
  //       )}

  //       {/* Helpful tip */}
  //       <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-md text-center">
  //         <p className="text-sm text-blue-700">
  //           💡 <strong>Tip:</strong> This process typically takes 2-5 minutes depending on the number and complexity of your documents.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  if (isAnalyzing || (analysisProgress && analysisProgress.analyzing > 0)) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative mb-6">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 bg-electric-blue rounded-full animate-pulse"></div>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-gray-800 mb-2">Processing Documents</h3>
      <p className="text-gray-600 text-center max-w-md">
        {analysisProgress ? 
          `${analysisProgress.completed}/${analysisProgress.total} documents processed. SOP generation will begin shortly.` :
          'The SOP will be generated automatically upon completion.'
        }
      </p>

      {/* Progress indicator if analysis progress is available */}
      {analysisProgress && analysisProgress.total > 0 && (
        <div className="w-64 mt-6">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-electric-blue h-2 rounded-full transition-all duration-300"
              style={{ width: `${(analysisProgress.completed / analysisProgress.total) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Processing...</span>
            <span>{Math.round((analysisProgress.completed / analysisProgress.total) * 100)}%</span>
          </div>
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-md text-center">
        <p className="text-sm text-blue-700">
          💡 <strong>Tip:</strong> This process typically takes 2-5 minutes depending on the number and complexity of your documents.
        </p>
      </div>
    </div>
  );
}

  if (isUpgrading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 max-w-2xl mx-auto">
        {/* Enhanced Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6 shadow-inner">
          <div
            className="bg-gradient-to-r from-electric-blue to-purple-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
            style={{ width: `${sopProgress?.progress || 0}%` }}
          ></div>
        </div>

        {/* Main Loading Animation */}
        <div className="relative mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-electric-blue rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Stage Message */}
        <div className="text-center mb-4">
          <p className="text-gray-700 text-lg font-medium mb-2">
            {sopProgress?.stage || 'Upgrading to Enhanced SOP...'}
          </p>
          <div className="flex items-center justify-center space-x-2 text-electric-blue">
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>

        {/* Progress Details */}
        <div className="text-sm text-gray-600 text-center bg-gray-50 rounded-lg p-4 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <span className="font-semibold text-electric-blue">{sopProgress?.progress || 0}%</span>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
            {sopProgress?.startTime && (
              <div>
                <span className="font-semibold text-green-600">
                  {Math.floor((Date.now() - sopProgress.startTime) / 60000)}:{String(Math.floor(((Date.now() - sopProgress.startTime) % 60000) / 1000)).padStart(2, '0')}
                </span>
                <p className="text-xs text-gray-500">Elapsed</p>
              </div>
            )}
            {sopProgress?.estimatedTime && sopProgress.estimatedTime > 0 && (
              <div>
                <span className="font-semibold text-orange-600">
                  ~{Math.ceil(sopProgress.estimatedTime / 60)} min
                </span>
                <p className="text-xs text-gray-500">Remaining</p>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 bg-blue-50 rounded p-3 border-l-4 border-blue-300">
            <p className="mb-1">🔬 <strong>What's happening:</strong> Our AI is analyzing your documents and creating comprehensive enterprise-grade SOP documentation</p>
            <p>📊 <strong>Expected output:</strong> 15k-25k word detailed procedural documentation with controls, risks, and compliance mapping</p>
          </div>

          {/* Live ETA — calibrated per-project from actual doc count/length/complexity,
              replaces the old static "3-8 min" claim which was inaccurate for
              multi-doc runs (see docs/jarvis-sop-performance-plan.md). */}
          {currentProject && (
            <SopEtaBanner
              projectId={currentProject.id}
              startedAt={enhancedStartedAt ?? undefined}
              progress={typeof sopProgress?.progress === 'number' ? sopProgress.progress / 100 : undefined}
              className="mt-3"
            />
          )}
        </div>
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-gray-400 mb-6">
          <FileText className="w-16 h-16" />
        </div>

        <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready to Generate SOP</h3>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Your documents have been analyzed and the process has been synthesized. Choose the type of SOP to generate.
        </p>

        {/* Data-driven ETA so the user knows what they're committing to before
            clicking. Falls silent on error — never blocks the Generate flow. */}
        {currentProject && (
          <SopEtaBanner projectId={currentProject.id} className="w-full max-w-lg mb-6" />
        )}

        {terminologyIncomplete && (
          <div className="w-full max-w-lg mb-6 bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 mb-1">
                  {pendingTermCount} term{pendingTermCount === 1 ? '' : 's'} still pending review
                </h4>
                <p className="text-xs text-amber-800 leading-relaxed">{pendingTermMessage}</p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {onGoToTerminology && (
                    <button
                      onClick={onGoToTerminology}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                    >
                      Go to Step 4: Approve Terminology
                    </button>
                  )}
                  {terminologyGateActive ? (
                    <button
                      onClick={() => setOverrideTerminologyGate(true)}
                      className="text-xs font-medium text-amber-700 underline hover:text-amber-900"
                    >
                      Generate anyway
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-amber-700">
                      Generation unlocked — Section 3.0 will be incomplete.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          {/* Basic SOP */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={generateBasicSOP}
              title={terminologyGateActive ? pendingTermMessage : undefined}
              disabled={isBasicSOPGenerating || terminologyGateActive}
              className="flex items-center space-x-3 px-8 py-3 bg-electric-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-base font-medium shadow-md"
            >
              {isBasicSOPGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  <span>Generate Basic SOP</span>
                </>
              )}
            </button>
            <p className="text-xs text-gray-400 text-center max-w-[180px]">Fast · 1–3 min · Standard format</p>
          </div>

          {/* Enhanced SOP */}
          <div className="flex flex-col items-center gap-2">
            {isEnhancingInProgress ? (
              <button
                type="button"
                onClick={cancelEnhancedSOP}
                className="flex items-center space-x-3 px-8 py-3 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-base font-medium shadow-md"
                title="Stop generation — the current AI call may finish, then no further calls run"
              >
                <Square className="w-5 h-5" />
                <span>Stop generation</span>
              </button>
            ) : (
            <button
              onClick={generateEnhancedSOP}
              title={terminologyGateActive ? pendingTermMessage : undefined}
              disabled={isBasicSOPGenerating || terminologyGateActive}
              className="flex items-center space-x-3 px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-base font-medium shadow-md"
            >
              <Wand2 className="w-5 h-5" />
              <span>Generate Enhanced SOP</span>
            </button>
            )}
            <p className="text-xs text-gray-400 text-center max-w-[180px]">Detailed · 3–8 min · With controls &amp; risks</p>
          </div>
        </div>

        {(isBasicSOPGenerating || isEnhancingInProgress) && (
          <p className="text-sm text-gray-500 mt-6 text-center max-w-sm">
            This usually takes a few minutes depending on document complexity.
          </p>
        )}
      </div>
    );
  }

  let sopData;
  try {
    sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
  } catch (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>Error parsing SOP content. It might be malformed JSON.</p>
        <pre className="mt-2 text-sm bg-red-100 p-2 rounded whitespace-pre-wrap">
          {sop.content}
        </pre>
      </div>
    );
  }

  // Additional safety check for sopData
  if (!sopData) {
    return (
      <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
        <p>SOP data is not available. Please try refreshing the page or re-generating the SOP.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
        >
          🔄 Refresh Page
        </button>
      </div>
    );
  }

  // Registry/revision section numbers depend on THIS SOP's actual section
  // structure -- a client format like Pacific Life renumbers Gaps Register to
  // 6.0 and drops Controls Registry entirely, and auto-appends Revision History
  // one past the last real section (7.0), not the generic format's 13.0.
  // Mirrors exportService.js's deriveRegistrySectionNumbers /
  // computeRevisionSectionNumber exactly -- see sopSectionNumbering.ts.
  //
  // hasInternalControlsRegistry: Array.isArray(sopData.controls_registry) is
  // true only for a client format that tracks controls as an internal
  // registry with no visible section (Pacific Life) -- passing it means
  // registryNums.controls resolves to null there instead of silently
  // falling back to the generic format's Section 8.0, which does not exist
  // in that document.
  const registryNums = deriveRegistrySectionNumbers(sopData.sections || [], {
    hasInternalControlsRegistry: Array.isArray(sopData.controls_registry),
  });
  const revisionSectionNumber = computeRevisionSectionNumber(sopData.sections || []);

  const processingMethod = sopData?.compliance_assessment?.processing_method;
  let sopStatusMessage = '';
  let sopStatusClass = '';

  // Determine if background processing is happening
  const isBackgroundProcessing = processingMethod && [
    'basic_immediate',
    'comprehensive_structured',
    'standard_analysis',
    'chunked_analysis'
  ].includes(processingMethod);

  const isEnhancedComplete = processingMethod && [
    'comprehensive_enhanced',
    'hybrid_enhanced',
    'single_prompt_enhanced'
  ].includes(processingMethod);

  // isEnhancingInProgress takes priority — prevents "ready" banner showing while actively generating
  if (isUpgrading) {
    sopStatusMessage = '';
  } else if (isEnhancingInProgress || isBackgroundProcessingDetected) {
    sopStatusMessage = sop
      ? '🔄 Enhanced SOP is being generated in the background. Displaying current version...'
      : '🔄 Enhanced SOP is being generated. Please wait...';
    sopStatusClass = 'bg-yellow-100 border border-yellow-300 text-yellow-800';
  } else if (isBackgroundProcessing) {
    sopStatusMessage = '🔄 Enhanced SOP is being generated in the background. Displaying current version...';
    sopStatusClass = 'bg-yellow-100 border border-yellow-300 text-yellow-800';
  } else if (isEnhancedComplete) {
    sopStatusMessage = '✅ Comprehensive Enhanced SOP is ready!';
    sopStatusClass = 'bg-green-100 border border-green-300 text-green-800';
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-2">
        {/* Title + Run Verifier + Export SOP — left column */}
        <div className="flex flex-col gap-2 shrink-0">
          <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
            <h2 className="text-xl font-semibold text-super-dark-purple">
              {sopType === 'enhanced' ? 'Comprehensive Enhanced SOP' : 'Standard Operating Procedure'}
            </h2>
            <AccuracyGateBadge
              gate={sopData.accuracy_gate}
            />
            <HelpTooltip
              title="About SOP Generation"
              variant="default"
              iconSize={16}
              content={
                <div>
                  <p className="mb-2">Jarvis SOPs are built from your actual project data:</p>
                  <ul className="list-disc pl-3 space-y-1">
                    <li><strong>Multi-document synthesis:</strong> Cross-references all uploaded documents</li>
                    <li><strong>Full provenance:</strong> Every step traces back to source documents</li>
                    <li><strong>Stakeholder-aware:</strong> RACI assignments from your approved list</li>
                    <li><strong>Process map aligned:</strong> Steps validated against your process map</li>
                    <li><strong>Compliance integrated:</strong> COSO, SOX, and Basel III frameworks</li>
                  </ul>
                </div>
              }
            />
          </div>
          {/* Run Verifier + Export SOP below title */}
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (!sop?.id || isVerifyingSOP) return;
                setIsVerifyingSOP(true);
                const toastId = 'verify-sop';
                try {
                  toast.loading('Running accuracy gate...', { id: toastId, duration: Infinity });
                  const enqueueRes = await api.post(`/api/analysis/sop/${sop.id}/verify`, { mode: 'standard' });
                  const data = enqueueRes.data || {};
                  if (data.status === 'completed' && data.accuracy_gate) {
                    toast.success(accuracyGateHeadline(data.accuracy_gate), { id: toastId, duration: 4000 });
                  } else {
                    const jobId = String(data.job_id || '');
                    if (!jobId) throw new Error('Backend did not return a job id');
                    await pollVerificationJob(jobId, {
                      onProgress: (s) => {
                        toast.loading(describeProgress(s.progress), { id: toastId, duration: Infinity });
                      }
                    });
                    toast.success('Verification complete', { id: toastId, duration: 3000 });
                  }
                  if (typeof loadAnalysisResults === 'function') {
                    await loadAnalysisResults();
                  }
                } catch (error: any) {
                  console.error('Verify SOP error:', error);
                  toast.error(error?.message || error?.response?.data?.message || 'Failed to run verifier', { id: toastId });
                } finally {
                  setIsVerifyingSOP(false);
                }
              }}
              disabled={!sop?.id || isVerifyingSOP}
              title={!sop?.id ? 'Generate an SOP first' : 'Run the accuracy gate: source diagram vs map vs §5 step lock. Not a claim-by-claim check against Process Synthesis.'}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white transition-all disabled:opacity-50 flex items-center space-x-1.5"
            >
              {isVerifyingSOP ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>Run Verifier</span>
                </>
              )}
            </button>
            <button
              onClick={async () => {
                if (!currentProject || isExportingSOP) return;
                setIsExportingSOP(true);
                const toastId = 'export-sop';
                try {
                  toast.loading('Preparing SOP document...', { id: toastId, duration: Infinity });
                  const sopContent = sop?.content
                    ? (typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content)
                    : null;
                  const stepLinkages: any[] = sopContent?.step_linkages || [];
                  const sectionsWithLinkage = (sopContent?.sections || []).map((section: any) => {
                    const linkage = stepLinkages.find(
                      (l: any) => l.sop_section_number === section.section_number && (l.confidence ?? 1) >= 0.6
                    );
                    return {
                      ...section,
                      step_linkage: linkage
                        ? { process_step_number: linkage.process_step_number, process_step_title: linkage.process_step_title }
                        : null,
                    };
                  });
                  const response = await api.post(`/api/export/sop/${currentProject.id}`, {
                    contentViewMode,
                    showMarkers,
                    sections: sectionsWithLinkage,
                    step_linkages: sopContent?.step_linkages || [],
                    title: sopContent?.title || sop?.title,
                    version: sopContent?.version || sop?.version,
                  });
                  const downloadUrl = response.data.download_url;
                  toast.loading('Downloading SOP file...', { id: toastId, duration: Infinity });
                  const fileResponse = await api.get(downloadUrl, { responseType: 'blob' });
                  const blob = new Blob([fileResponse.data], {
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${currentProject.name}_SOP.docx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);

                  // Controls Excel (Step 3B) — the same export action already
                  // produced this alongside the Word doc (see routes/export.js
                  // POST /api/export/sop/:projectId); only present when this
                  // client format tracks controls internally (Pacific Life).
                  // No separate "Generate Controls Excel" step — it downloads
                  // automatically right after the Word doc, from this same click.
                  const controlsExcelUrl = response.data.controls_excel_download_url;
                  if (controlsExcelUrl) {
                    const excelResponse = await api.get(controlsExcelUrl, { responseType: 'blob' });
                    const excelBlob = new Blob([excelResponse.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    const excelUrl = window.URL.createObjectURL(excelBlob);
                    const excelLink = document.createElement('a');
                    excelLink.href = excelUrl;
                    excelLink.download = `${currentProject.name}_Controls.xlsx`;
                    document.body.appendChild(excelLink);
                    excelLink.click();
                    document.body.removeChild(excelLink);
                    window.URL.revokeObjectURL(excelUrl);
                  }

                  toast.success(
                    controlsExcelUrl ? 'SOP and Controls Excel exported successfully!' : 'SOP exported successfully!',
                    { id: toastId, duration: 3000 }
                  );
                } catch (error: any) {
                  console.error('Export SOP error:', error);
                  const errData = error?.response?.data;
                  if (errData?.error === 'verification_hard_block') {
                    toast.error('Cannot export: SOP confidence is below the 60% minimum threshold. Please review and improve the SOP before exporting.', { id: toastId, duration: 6000 });
                  } else {
                    toast.error('Failed to export SOP. Please try again.', { id: toastId });
                  }
                } finally {
                  setIsExportingSOP(false);
                }
              }}
              disabled={!analysisResults?.sop || isExportingSOP || isBasicSOPGenerating || isEnhancingInProgress}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-electric-blue text-white hover:bg-darker-blue transition-all disabled:opacity-50 flex items-center space-x-1.5"
            >
              {isExportingSOP ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5" />
                  <span>Export SOP</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Controls — right */}
        <div className="flex items-center gap-2 flex-1 ml-4">

          {/* Left group: Regenerate / Tools / Filters / Markers */}
          <div className="flex items-center gap-2">

          <button
            onClick={() => setPendingRegenerateType('basic')}
            title={terminologyIncomplete ? pendingTermMessage : undefined}
            disabled={isBasicSOPGenerating || isEnhancingInProgress}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all disabled:opacity-50"
          >
            {isBasicSOPGenerating ? (
              <><div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> Generating...</>
            ) : (
              <><Wand2 className="w-3 h-3" /> Generate Basic SOP</>
            )}
          </button>

          <button
            onClick={() => setPendingRegenerateType('enhanced')}
            title={terminologyIncomplete ? pendingTermMessage : undefined}
            disabled={isEnhancingInProgress || isBasicSOPGenerating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-all disabled:opacity-50"
          >
            {isEnhancingInProgress ? (
              <><div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /> Generating...</>
            ) : (
              <><Wand2 className="w-3 h-3" /> Generate Enhanced SOP</>
            )}
          </button>
          {isEnhancingInProgress && (
            <button
              type="button"
              onClick={cancelEnhancedSOP}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"
              title="Stop generation — the current AI call may finish, then no further calls run"
            >
              <Square className="w-3 h-3" /> Stop
            </button>
          )}

          {allSOPs.length > 0 && (
            <button
              onClick={() => setShowClearConfirm(true)}
              disabled={isClearingSOP}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200 transition-all disabled:opacity-50"
              title="Delete all SOPs for this project"
            >
              {isClearingSOP ? (
                <><div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> Clearing...</>
              ) : (
                <><Trash2 className="w-3 h-3" /> Clear SOP</>
              )}
            </button>
          )}

          <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
            <ToolbarDropdown
              label="Tools"
              icon={<Settings className="w-3 h-3" />}
              showActiveIndicator={true}
              headerHelp={{
                title: 'AI-Powered Analysis Tools',
                body: (
                  <span>These tools use your project's full context (documents, stakeholders, process maps) to perform analyses impossible with generic AI tools.</span>
                ),
              }}
              items={[
                {
                  key: 'check-docs',
                  label: isComparingWithDocs ? 'Comparing...' : 'Check against Documentation',
                  icon: <FileText className="w-4 h-4" />,
                  onClick: () => {
                    if (!showToolDocDropdown) loadAvailableToolPlatforms();
                    setShowToolDocDropdown(!showToolDocDropdown);
                  },
                  loading: isComparingWithDocs,
                  disabled: isComparingWithDocs,
                  helpContent: {
                    title: 'Documentation Comparison Engine',
                    body: (
                      <ul className="list-disc pl-3 space-y-1">
                        <li>Compares your SOP against vendor tool documentation</li>
                        <li>Identifies gaps where your process diverges from platform capabilities</li>
                        <li>Suggests tool-specific enhancements</li>
                      </ul>
                    ),
                  },
                },
                {
                  key: 'generate-checklist',
                  label: 'Generate Operational Checklist',
                  icon: <ClipboardList className="w-4 h-4" />,
                  onClick: async () => {
                    if (!currentProject || !sop) return;
                    const toastId = 'gen-checklist';
                    try {
                      toast.loading('Generating operational checklist...', { id: toastId, duration: Infinity });
                      const res = await api.post(`/api/analysis/generate-checklist/${currentProject.id}`, {
                        sop_id: sop.id,
                      });
                      toast.success('Checklist generated! Check the export.', { id: toastId });
                      if (res.data?.download_url) {
                        const fileRes = await api.get(res.data.download_url, { responseType: 'blob' });
                        const blob = new Blob([fileRes.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${currentProject.name}_Operational_Checklist.docx`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      }
                    } catch (err) {
                      console.error('Checklist error:', err);
                      toast.error('Failed to generate checklist', { id: toastId });
                    }
                  },
                  helpContent: {
                    title: 'Operational Checklist',
                    body: (
                      <span>Generates a step-by-step operational checklist from your SOP that teams can use for daily execution and compliance verification.</span>
                    ),
                  },
                },
                {
                  key: 'raci',
                  label: hasRACIData ? 'View RACI Data' : 'Generate RACI',
                  icon: <Users className="w-4 h-4" />,
                  onClick: () => window.location.href = hasRACIData
                    ? `/app/raci-matrix?projectId=${currentProject?.id}`
                    : `/app/raci-matrix?projectId=${currentProject?.id}&autoGenerate=true`,
                  active: !!hasRACIData,
                  helpContent: {
                    title: 'RACI Matrix Generation',
                    body: (
                      <ul className="list-disc pl-3 space-y-1">
                        <li>Auto-generates Responsible/Accountable/Consulted/Informed assignments</li>
                        <li>Uses your approved stakeholders and process map steps</li>
                        <li>Auto-syncs when stakeholders or process map changes</li>
                      </ul>
                    ),
                  },
                },
              ]}
            />
          </div>

          {/* Filters dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                contentViewMode !== 'all' || !showMarkers
                  ? 'bg-purple-100 text-purple-700 border border-purple-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-3 h-3" />
              Filters
              {contentViewMode !== 'all' && (
                <span className="bg-purple-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                  {contentViewMode === 'original' ? 'Original Only' : 'AI Added Only'}
                </span>
              )}
              {!showMarkers && contentViewMode === 'all' && (
                <span className="bg-purple-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                  No Markers
                </span>
              )}
            </button>
            {showFiltersDropdown && (
              <>
              <div className="fixed inset-0 z-20" onClick={() => setShowFiltersDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-2">
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Content View</div>
                {(['all', 'original', 'additions'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => { setContentViewMode(mode); }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                      contentViewMode === mode ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                      contentViewMode === mode ? 'border-purple-500' : 'border-gray-300'
                    }`}>
                      {contentViewMode === mode && <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />}
                    </span>
                    {mode === 'all' ? 'Show All' : mode === 'original' ? 'Original Only' : 'AI Added Only'}
                  </button>
                ))}
                <div className="border-t border-gray-100 my-1" />
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Display</div>
                <label className="w-full px-3 py-2 text-sm text-gray-700 flex items-center justify-between cursor-pointer hover:bg-gray-50">
                  <span>SOP Markers</span>
                  <div
                    onClick={() => setShowMarkers(!showMarkers)}
                    className={`relative w-8 h-4.5 rounded-full transition-colors cursor-pointer ${showMarkers ? 'bg-purple-500' : 'bg-gray-300'}`}
                    style={{ width: 32, height: 18 }}
                  >
                    <div
                      className="absolute top-0.5 rounded-full bg-white shadow transition-transform"
                      style={{ width: 14, height: 14, transform: showMarkers ? 'translateX(16px)' : 'translateX(2px)' }}
                    />
                  </div>
                </label>
              </div>
              </>
            )}
          </div>

          {/* Markers legend — inline with toolbar */}
          {showMarkers && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium text-gray-600">Markers:</span>
              {[
                { color: 'bg-red-500', label: 'Gap' },
                { color: 'bg-purple-500', label: 'Control' },
                { color: 'bg-blue-500', label: 'Screenshot' },
                { color: 'bg-green-500', label: 'Flowchart' },
                { color: 'bg-orange-500', label: 'Conflict' },
                { color: 'bg-gray-400', label: 'Source' },
                { color: 'bg-yellow-500', label: 'Assumption' },
              ].map(m => (
                <span key={m.label} className="flex items-center gap-1">
                  <span className={`w-2 h-2 ${m.color} rounded-full`} />
                  {m.label}
                </span>
              ))}
            </div>
          )}

          </div>{/* end left group */}


        </div>
      </div>

      {/* Stage 3 IR Verification Panel — sits directly under the buttons row */}
      {sopData?.stage3_verification && !sopData.stage3_verification.skipped && (() => {
        const v = sopData.stage3_verification;

        // Frontend pass-phrase filter — mirrors backend post-processor as defence-in-depth.
        // Strips any entry where the issue text itself concludes there is no real problem.
        const ACTOR_PASS_PHRASES = [
          'no actor mismatch', 'actor assignment is correct', 'correctly assigns',
          'no mismatch', 'actor is correct', 'correctly assigned', 'assignment correct',
          'no issue detected', 'no issue', 'no cross-lane issue', 'attribution is appropriate',
          'best-fit actor matches', 'reasonable fit for this actor',
          'plausible fit for this actor', 'no clear better-fit actor',
          'matches the sop', 'ir matches', 'sop. no issue',
        ];
        const DECISION_PASS_PHRASES = [
          'decision gate passes', 'both paths are', 'both paths covered',
          'both yes and no', 'no decision issue', 'gate passes',
        ];
        const hasPassPhrase = (text: string, phrases: string[]) => {
          const lower = (text || '').toLowerCase();
          return phrases.some(p => lower.includes(p));
        };

        const allActorIssues: Array<{ step_number: number; step_name: string; expected_actor: string; issue: string }> =
          (v.actor_issues || []).filter((e: any) => !hasPassPhrase(e.issue, ACTOR_PASS_PHRASES));
        const allDecisionIssues: Array<{ step_number: number; step_name: string; issue: string }> =
          (v.decision_issues || []).filter((e: any) => !hasPassPhrase(e.issue, DECISION_PASS_PHRASES));
        const allFabricationIssues: Array<{ reference: string; issue: string }> = v.fabrication_issues || [];
        const allSequenceIssues: Array<{ sop_step: string; sop_content: string; expected_ir_position: number; issue: string }> = v.sequence_issues || [];
        const allDuplicateIssues: Array<{ actor: string; repeats: number; excerpt: string; issue: string }> = v.duplicate_issues || [];

        // Apply per-issue dismissals
        const actorIssues = allActorIssues.filter((_, idx) => !dismissedStage3Issues.has(`a${idx}`));
        const decisionIssues = allDecisionIssues.filter((_, idx) => !dismissedStage3Issues.has(`d${idx}`));
        const fabricationIssues = allFabricationIssues.filter((_, idx) => !dismissedStage3Issues.has(`f${idx}`));
        const sequenceIssues = allSequenceIssues.filter((_, idx) => !dismissedStage3Issues.has(`s${idx}`));
        const duplicateIssues = allDuplicateIssues.filter((_, idx) => !dismissedStage3Issues.has(`u${idx}`));

        const totalIssues = actorIssues.length + decisionIssues.length + fabricationIssues.length + sequenceIssues.length + duplicateIssues.length;
        const passed = totalIssues === 0;
        // Recompute the score live from the current (post-fix/dismiss) issue counts
        // using the same weighted formula the backend uses to compute overall_score
        // (enhancedSOPService.js: -15 per actor issue, -10 per decision issue, -20 per
        // fabrication issue, -10 per sequence issue, floored at 0). v.overall_score is
        // a snapshot from generation time and never reflects fixes/dismissals applied
        // afterward, so it can't be used directly here — only as the initial baseline
        // before any issues have been fixed or dismissed.
        const score = v.overall_score == null ? null : Math.max(0, 100
          - actorIssues.length * 15
          - decisionIssues.length * 10
          - fabricationIssues.length * 20
          - sequenceIssues.length * 10
          - duplicateIssues.length * 15
        );
        const checks = [actorIssues, decisionIssues, sequenceIssues, fabricationIssues, duplicateIssues];
        const checksPassed = checks.filter(arr => arr.length === 0).length;
        const borderColor = passed ? 'border-green-500' : 'border-amber-500';
        const headerBg = passed ? 'from-green-50 to-white' : 'from-amber-50 to-white';

        // Open/close a finding's inline fix box. Prefills the draft with the flagged
        // text itself so the user is editing/correcting it directly, not starting blank.
        const toggleFixBox = (key: string, prefillText: string) => {
          if (openStage3FixKey === key) {
            setOpenStage3FixKey(null);
          } else {
            setOpenStage3FixKey(key);
            setStage3FixDraft(prefillText);
          }
        };

        return (
          <div className={`bg-white border overflow-hidden rounded-lg border-l-4 ${borderColor} mb-4`}>
            <div
              className={`w-full px-6 py-4 bg-gradient-to-r ${headerBg} ${isStage3Expanded ? 'border-b border-gray-200' : ''} flex items-center justify-between`}
            >
              <button
                onClick={() => setIsStage3Expanded(x => !x)}
                className="flex items-center gap-3 flex-1 text-left hover:opacity-90 transition-colors"
              >
                <ShieldCheck className={`w-5 h-5 ${passed ? 'text-green-600' : 'text-amber-600'}`} />
                <span className="font-medium text-gray-800">IR Verification Report</span>
                {passed ? (
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">
                    {checksPassed}/{checks.length} checks passed
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">
                    {totalIssues} issue{totalIssues !== 1 ? 's' : ''} found
                  </span>
                )}
                {score !== null && (
                  <span className="text-xs text-gray-500 font-medium">Score: {score}/100</span>
                )}
              </button>
              <button
                onClick={() => setIsStage3Expanded(x => !x)}
                className="p-2 hover:opacity-90 transition-colors shrink-0"
                title={isStage3Expanded ? 'Collapse' : 'Expand'}
              >
                {isStage3Expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
            </div>

            {isStage3Expanded && (
              <div className="px-6 py-4">
                {/* 4 check cards */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {[
                    { label: 'Actor Attribution', issues: actorIssues, color: 'red' },
                    { label: 'Decision Paths', issues: decisionIssues, color: 'amber' },
                    { label: 'Step Sequence', issues: sequenceIssues, color: 'purple' },
                    { label: 'Fabrication Check', issues: fabricationIssues, color: 'orange' },
                    { label: 'Repeated Text', issues: duplicateIssues, color: 'red' },
                  ].map(({ label, issues, color }) => {
                    const count = issues.length;
                    const ok = count === 0;
                    return (
                      <div key={label} className={`rounded-lg border p-3 flex flex-col gap-1 ${ok ? 'border-green-200 bg-green-50' : `border-${color}-200 bg-${color}-50`}`}>
                        <span className="text-xs font-semibold text-gray-600">{label}</span>
                        <div className={`flex items-center gap-1.5 text-sm font-medium ${ok ? 'text-green-700' : `text-${color}-700`}`}>
                          {ok
                            ? <><CheckCircle className="w-4 h-4" /> Passed</>
                            : <><AlertTriangle className="w-4 h-4" /> {count} issue{count !== 1 ? 's' : ''}</>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Issue details with inline fix + dismiss */}
                {totalIssues > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Issue Details</p>
                    {actorIssues.map((issue, idx) => {
                      const key = `a${idx}`;
                      return (
                      <React.Fragment key={key}>
                      <div className="flex gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-red-700">Step {issue.step_number} — Actor Issue</p>
                          <p className="text-xs text-red-600 mt-0.5">{issue.issue}</p>
                        </div>
                        <button onClick={() => toggleFixBox(key, issue.issue)} className="text-red-600 hover:text-red-800 shrink-0 mt-0.5 p-1" title={openStage3FixKey === key ? 'Close' : 'Edit and fix this finding'}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => dismissStage3IssuePersisted('a', allActorIssues.indexOf(issue))} className="text-red-300 hover:text-red-500 shrink-0 mt-0.5" title="Dismiss finding">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openStage3FixKey === key && (
                        <div className="bg-white border border-red-200 rounded-lg px-4 py-3 -mt-1">
                          <p className="text-xs text-gray-500 mb-2">Correct the issue below, then apply the fix to Section 5.0 (Procedure Requirements).</p>
                          <textarea
                            value={stage3FixDraft}
                            onChange={e => setStage3FixDraft(e.target.value)}
                            rows={3}
                            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400"
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button onClick={() => setOpenStage3FixKey(null)} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">Cancel</button>
                            <button
                              onClick={async () => {
                                await applyStage3Fix('5.0', issue.step_name || '', stage3FixDraft, { family: 'a', arrayIndex: allActorIssues.indexOf(issue) });
                                setOpenStage3FixKey(null);
                              }}
                              disabled={isSavingStage3Fix}
                              className="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                              {isSavingStage3Fix ? 'Applying...' : 'Apply Fix'}
                            </button>
                          </div>
                        </div>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {decisionIssues.map((issue, idx) => {
                      const key = `d${idx}`;
                      return (
                      <React.Fragment key={key}>
                      <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-amber-700">Step {issue.step_number} — Decision Path Issue</p>
                          <p className="text-xs text-amber-600 mt-0.5">{issue.issue}</p>
                        </div>
                        <button onClick={() => toggleFixBox(key, issue.issue)} className="text-amber-600 hover:text-amber-800 shrink-0 mt-0.5 p-1" title={openStage3FixKey === key ? 'Close' : 'Edit and fix this finding'}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => dismissStage3IssuePersisted('d', allDecisionIssues.indexOf(issue))} className="text-amber-300 hover:text-amber-500 shrink-0 mt-0.5" title="Dismiss finding">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openStage3FixKey === key && (
                        <div className="bg-white border border-amber-200 rounded-lg px-4 py-3 -mt-1">
                          <p className="text-xs text-gray-500 mb-2">Correct the issue below, then apply the fix to Section 5.0 (Procedure Requirements).</p>
                          <textarea
                            value={stage3FixDraft}
                            onChange={e => setStage3FixDraft(e.target.value)}
                            rows={3}
                            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button onClick={() => setOpenStage3FixKey(null)} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">Cancel</button>
                            <button
                              onClick={async () => {
                                await applyStage3Fix('5.0', issue.step_name || '', stage3FixDraft, { family: 'd', arrayIndex: allDecisionIssues.indexOf(issue) });
                                setOpenStage3FixKey(null);
                              }}
                              disabled={isSavingStage3Fix}
                              className="px-3 py-1 text-xs bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
                            >
                              {isSavingStage3Fix ? 'Applying...' : 'Apply Fix'}
                            </button>
                          </div>
                        </div>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {fabricationIssues.map((issue, idx) => {
                      const key = `f${idx}`;
                      return (
                      <React.Fragment key={key}>
                      <div className="flex gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-orange-700">Fabrication Detected</p>
                          <p className="text-xs text-orange-600 mt-0.5">"{issue.reference}" — {issue.issue}</p>
                        </div>
                        <button onClick={() => toggleFixBox(key, issue.reference || '')} className="text-orange-600 hover:text-orange-800 shrink-0 mt-0.5 p-1" title={openStage3FixKey === key ? 'Close' : 'Edit and fix this finding'}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => dismissStage3IssuePersisted('f', allFabricationIssues.indexOf(issue))} className="text-orange-300 hover:text-orange-500 shrink-0 mt-0.5" title="Dismiss finding">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openStage3FixKey === key && (
                        <div className="bg-white border border-orange-200 rounded-lg px-4 py-3 -mt-1">
                          <p className="text-xs text-gray-500 mb-2">Correct the fabricated reference below, then apply the fix to Section 11.0 (Related Policies and Supporting Documents).</p>
                          <textarea
                            value={stage3FixDraft}
                            onChange={e => setStage3FixDraft(e.target.value)}
                            rows={3}
                            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button onClick={() => setOpenStage3FixKey(null)} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">Cancel</button>
                            <button
                              onClick={async () => {
                                await applyStage3Fix('11.0', issue.reference || '', stage3FixDraft, { family: 'f', arrayIndex: allFabricationIssues.indexOf(issue) });
                                setOpenStage3FixKey(null);
                              }}
                              disabled={isSavingStage3Fix}
                              className="px-3 py-1 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                            >
                              {isSavingStage3Fix ? 'Applying...' : 'Apply Fix'}
                            </button>
                          </div>
                        </div>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {sequenceIssues.map((issue, idx) => {
                      const key = `s${idx}`;
                      return (
                      <React.Fragment key={key}>
                      <div className="flex gap-3 bg-purple-50 border border-purple-200 rounded-lg px-4 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-purple-700">SOP Step {issue.sop_step} — Sequence Issue</p>
                          <p className="text-xs text-purple-600 mt-0.5">{issue.issue}</p>
                        </div>
                        <button onClick={() => toggleFixBox(key, issue.sop_content || '')} className="text-purple-600 hover:text-purple-800 shrink-0 mt-0.5 p-1" title={openStage3FixKey === key ? 'Close' : 'Edit and fix this finding'}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => dismissStage3IssuePersisted('s', allSequenceIssues.indexOf(issue))} className="text-purple-300 hover:text-purple-500 shrink-0 mt-0.5" title="Dismiss finding">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openStage3FixKey === key && (
                        <div className="bg-white border border-purple-200 rounded-lg px-4 py-3 -mt-1">
                          <p className="text-xs text-gray-500 mb-2">Correct the step content below, then apply the fix to Section 5.0 (Procedure Requirements).</p>
                          <textarea
                            value={stage3FixDraft}
                            onChange={e => setStage3FixDraft(e.target.value)}
                            rows={3}
                            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button onClick={() => setOpenStage3FixKey(null)} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md">Cancel</button>
                            <button
                              onClick={async () => {
                                await applyStage3Fix('5.0', issue.sop_content || '', stage3FixDraft, { family: 's', arrayIndex: allSequenceIssues.indexOf(issue) });
                                setOpenStage3FixKey(null);
                              }}
                              disabled={isSavingStage3Fix}
                              className="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                            >
                              {isSavingStage3Fix ? 'Applying...' : 'Apply Fix'}
                            </button>
                          </div>
                        </div>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {duplicateIssues.map((issue, idx) => {
                      const key = `u${idx}`;
                      return (
                      <React.Fragment key={key}>
                      <div className="flex gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-red-700">Repeated Text — "{issue.actor}" appears {issue.repeats} times in a row</p>
                          <p className="text-xs text-red-600 mt-0.5">{issue.excerpt}</p>
                        </div>
                        <button onClick={() => dismissStage3IssuePersisted('u', allDuplicateIssues.indexOf(issue))} className="text-red-300 hover:text-red-500 shrink-0 mt-0.5" title="Dismiss finding">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {totalIssues === 0 && (
                  <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    All checks passed. Actors, decision paths, step sequence, and policy references are accurate.
                  </p>
                )}

                {/* Direct access to Step 3 (Validate Process Map) — findings above are
                    checks against the underlying process map, so fixing the root cause
                    (rather than just editing the generated SOP text) often means going
                    back to the process map itself. Surfaced here so that's reachable
                    without leaving Step 4. */}
                {onGoToProcessMap && (
                  <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      onClick={onGoToProcessMap}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-purple-600 transition-colors"
                      title="Open Step 3 — Validate Process Map to adjust the underlying process map"
                    >
                      <GitBranch className="w-3.5 h-3.5" />
                      Adjust in Validate Process Map
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {showToolDocDropdown && (
        <div className="w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">Select platform(s) to compare against:</p>
          {availableToolPlatforms.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">No documentation uploaded yet. Ask your admin to upload tool docs in Admin &gt; Workflow Tools.</p>
          ) : (
            <div className="space-y-1 mb-3">
              {availableToolPlatforms.map(platform => (
                <label key={platform} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedToolPlatforms.includes(platform)}
                    onChange={e => setSelectedToolPlatforms(prev =>
                      e.target.checked ? [...prev, platform] : prev.filter(p => p !== platform)
                    )}
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm capitalize text-gray-700">{platform}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={handleCompareWithToolDocs}
              disabled={!selectedToolPlatforms.length}
              className="flex-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run Comparison
            </button>
            <button
              onClick={() => setShowToolDocDropdown(false)}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}


      {sopStatusMessage && !isStatusMessageDismissed && (
        <div className={`${sopStatusClass} rounded p-3 mb-4 ${isBackgroundProcessingDetected ? 'animate-pulse' : ''}`}>
          <div className="flex items-center justify-between">
            <span>{sopStatusMessage}</span>
            <div className="flex items-center space-x-2">
              {(isEnhancingInProgress || isBackgroundProcessingDetected) && (
                <button
                  type="button"
                  onClick={cancelEnhancedSOP}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50"
                  title="Stop generation"
                >
                  <Square className="w-3 h-3" /> Stop
                </button>
              )}
              {isBackgroundProcessingDetected && (
                <>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <span className="text-xs text-gray-600 ml-2">Auto-refreshing...</span>
                </>
              )}
              <button
                onClick={() => setIsStatusMessageDismissed(true)}
                className="ml-2 p-1 hover:bg-black/10 rounded transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No SOP for selected tab */}
      {!sop && allSOPs.length > 0 && (
        isEnhancingInProgress ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center mb-6">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-yellow-800 font-medium">Generating Enhanced SOP...</p>
              <p className="text-yellow-600 text-sm">This may take a few minutes. The page will update automatically when ready.</p>
              <button
                type="button"
                onClick={cancelEnhancedSOP}
                className="mt-1 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                title="Stop generation — the current AI call may finish, then no further calls run"
              >
                <Square className="w-4 h-4" /> Stop generation
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center mb-6">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-amber-900 mb-2">
              No {activeSOPTab === 'basic' ? 'Basic' : activeSOPTab === 'enhanced' ? 'Enhanced' : 'Process Map'} SOP Found
            </h3>
            <p className="text-amber-700 mb-4">
              This type of SOP hasn't been generated yet. Try generating it using the buttons above.
            </p>
          </div>
        )
      )}

      {sop && (
      <>
      <DownstreamStalenessBanner
        staleness={sop.downstream_staleness}
        artifactLabel="SOP"
        onRegenerate={activeSOPTab === 'enhanced' ? generateEnhancedSOP : undefined}
        isRegenerating={isEnhancingInProgress}
        className="mb-6"
      />

      {/* Evidence Analysis Section */}
      {sopData?.evidence_analysis && (
        <div className="bg-white border overflow-hidden rounded-lg border-l-4 border-amber-800">
          <button
            onClick={() => setIsEvidenceExpanded(!isEvidenceExpanded)}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-50 to-white border-b border-gray-200 flex items-center justify-between hover:bg-green-50 transition-colors"
          >
            <div className="flex items-center">
              <Info className="w-5 h-5 mr-2" />
              <span className="font-medium">Evidence Analysis</span>
              <Tooltip text="Analysis of information gaps, assumptions made, and source attribution for audit trail and compliance purposes." placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
              {/* Count badges */}
              <div className="flex items-center ml-4 space-x-2">
                {sopData.evidence_analysis.information_gaps?.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.information_gaps.length} Gaps
                  </span>
                )}
                {sopData.evidence_analysis.assumptions_made?.length > 0 && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.assumptions_made.length} Assumptions
                  </span>
                )}
                {sopData.evidence_analysis.source_attribution?.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.source_attribution.length} Sources
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center text-gray-500">
              <span className="text-sm mr-2">{isEvidenceExpanded ? 'Collapse' : 'Expand'}</span>
              {isEvidenceExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>

          {/* Collapsible Content */}
          {isEvidenceExpanded && (
            <>
          {/* Information Gaps */}
          {sopData?.evidence_analysis?.information_gaps && sopData.evidence_analysis.information_gaps.length > 0 && (
            <div className="mb-6">
              <h5 className="font-medium text-red-700 mb-2 flex items-center p-4">
                <AlertCircle className="w-4 h-4 mr-1" />
                Information Gaps Requiring SME Input
              </h5>
              <div className="space-y-3 p-4">
                {sopData.evidence_analysis.information_gaps.map((gap: any, idx: number) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-red-800">
                        {gap.gap_type || 'Missing Information'}
                      </span>
                      <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                        {gap.section_affected || 'General'}
                      </span>
                    </div>
                    <p className="text-sm text-red-700 mb-2">{gap.description}</p>
                    {gap.sme_input_required && (
                      <p className="text-xs text-red-600 italic">
                        <strong>SME Question:</strong> {gap.sme_input_required}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4">
                <button
                  onClick={async () => {
                    if (!currentProject) return;
                    try {
                      toast.loading('Syncing gaps to interview questions...', { id: 'gap-sync' });
                      const response = await api.post(`/api/analysis/gap-sync/${currentProject.id}`);
                      toast.success(`Generated ${response.data.questions_generated} interview questions from ${response.data.gaps_detected} gaps`, { id: 'gap-sync' });
                    } catch (error) {
                      toast.error('Failed to sync gaps', { id: 'gap-sync' });
                    }
                  }}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-orange-700 transition-all flex items-center justify-center space-x-2"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Auto-Generate Interview Questions from Gaps</span>
                </button>
              </div>
            </div>
          )}

          {/* Assumptions Made */}
          {sopData?.evidence_analysis?.assumptions_made && sopData.evidence_analysis.assumptions_made.length > 0 && (
            <div className="mb-6">
              <h5 className="font-medium text-yellow-700 mb-2 flex items-center p-4">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Assumptions Made
              </h5>
              <div className="space-y-3 px-4">
                {sopData.evidence_analysis.assumptions_made.map((assumption: any, idx: number) => (
                  <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800 mb-1">{assumption.assumption}</p>
                    <p className="text-xs text-yellow-700 mb-2">{assumption.rationale}</p>
                    {assumption.validation_needed && (
                      <p className="text-xs text-yellow-600 italic">
                        <strong>Validation Needed:</strong> {assumption.validation_needed}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Source Attribution */}
          {sopData?.evidence_analysis?.source_attribution && sopData.evidence_analysis.source_attribution.length > 0 && (
            <div>
              <h5 className="font-medium text-blue-700 mb-2 flex items-center p-4">
                <FileText className="w-4 h-4 mr-1" />
                Source Attribution
              </h5>
              <div className="space-y-2 px-4 pb-4">
                {sopData.evidence_analysis.source_attribution.map((source: any, idx: number) => (
                  <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-blue-800">
                        {source.source_document || 'Unknown Document'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${source.confidence_level === 'high' ? 'bg-green-100 text-green-800' :
                        source.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                        {source.confidence_level || 'unknown'} confidence
                      </span>
                    </div>
                    <p className="text-xs text-blue-600">
                      Section: {source.section || 'General'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Gaps & Deviations for SOP */}

      {/* Render SOP Content */}
      <div className="space-y-4">
        {/* Approved terminology status banner */}
        {terminologyDefinitions.length > 0 ? (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-1">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-purple-900">Terminology Approved</h4>
                <p className="text-xs text-purple-700">
                  {terminologyDefinitions.length} approved terminology term{terminologyDefinitions.length !== 1 ? 's' : ''} available.
                  To reflect the latest approved terms in this SOP's Key Definitions, use Generate SOP — it rebuilds the full document, including this section, using the current approved terminology.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <span
            title="Please approve terminology from the Terminology Analysis page to enable Key Definitions auto-generation."
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs text-gray-600 mr-2"
          >
            <Info className="w-3 h-3" />
            No approved terminology
          </span>
        )}
        {(!approvedMapInfo || approvedMapInfo.validation_status !== 'validated') && (
          <button
            onClick={onGoToProcessMap}
            title={!approvedMapInfo
              ? 'No process map generated yet. Generate and approve a Level 3 process map in Step 3 for a higher-quality SOP.'
              : 'Process map is not yet approved. Approve it in Step 3 for a higher-quality SOP.'}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-xs text-blue-700 hover:bg-blue-100 transition-colors mb-4"
          >
            <AlertCircle className="w-3 h-3" />
            {!approvedMapInfo ? 'Process map not generated' : 'Process map not approved'}
          </button>
        )}

        {/* Aligned/Deviation Areas summary with tooltip */}
        {sopData?.template_alignment && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
            <div className="flex items-center mb-2">
              <span className="font-semibold text-green-700 mr-2">Aligned Areas</span>
              <Tooltip text={`"Aligned Areas" are process steps or controls that match the uploaded template's requirements and best practices.\n\nFactors considered:\n- Direct matches to template sections\n- Use of required controls, approvals, and roles\n- Adherence to regulatory and industry standards\n\nRisk frameworks referenced: COSO, SOX, Basel III, and any uploaded templates.`} placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            <ul className="list-disc ml-6 text-green-900">
              {sopData?.template_alignment?.aligned_areas?.length ? sopData.template_alignment.aligned_areas.map((area: string, idx: number) => (
                <li key={idx}>{area}</li>
              )) : <li>No aligned areas found.</li>}
            </ul>
            <div className="flex items-center mt-4 mb-2">
              <span className="font-semibold text-red-700 mr-2">Deviation Areas</span>
              <Tooltip text={`"Deviation Areas" are steps or controls that do not fully align with the template or best practices.\n\nFactors considered:\n- Missing required sections or controls\n- Gaps in compliance with regulatory or template standards\n- Unusual process flows or lack of documentation\n\nRisk frameworks referenced: COSO, SOX, Basel III, and any uploaded templates.`} placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            <ul className="list-disc ml-6 text-red-900">
              {sopData?.template_alignment?.deviation_areas?.length ? sopData.template_alignment.deviation_areas.map((area: string, idx: number) => (
                <li key={idx}>{area}</li>
              )) : <li>No deviation areas found.</li>}
            </ul>
          </div>
        )}

        {/* SOP Header — sits directly above Section 1.0 (Overview) */}
        <div className="bg-white border overflow-hidden rounded-lg border-l-4 border-green-500">
          <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-white border-b border-gray-200 flex justify-between items-center">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-md font-semibold mb-0">{sopData?.title || sop?.title || 'Standard Operating Procedure'}</h3>
              <AccuracyGateBadge
                gate={sopData?.accuracy_gate}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Version:</span>
                <p>{sopData?.version || sop?.version || '1.0'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Effective Date:</span>
                <p>{sopData?.effective_date || new Date().toLocaleDateString()}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Compliance Score:</span>
                <p>{globalComplianceScore !== null ? `${globalComplianceScore}%` : 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <p className="text-green-600">Active</p>
              </div>
            </div>
          </div>
        </div>

        {sopData?.sections?.map((section: any, index: number) => {
          // Word already suppresses an accidentally generated standalone
          // Controls Registry / Review and Approval section
          // (ExportService.isExcludedFromWord) -- apply the same centralized
          // rule here so a model hallucination against prompt instructions
          // can't show a duplicate/forbidden section in the browser that
          // Word hides. Returning null here (not filtering the array) keeps
          // every other index/position-based feature below (Add Section,
          // drag-and-drop, PM linkage lookups) untouched.
          if (isExcludedFromBrowserRender(section.section_title || section.title)) {
            return null;
          }

          const isEditing = editingSectionIndex === index;

          // Check both frontend state and backend status fields
          // modification_status: user edit tracking (user_modified, user_approved)
          // validation_status: preservation tracking (human_verified, human_modified, ai_generated)
          const backendModificationStatus = section.modification_status;
          const backendValidationStatus = section.validation_status;
          const frontendStatus = editedSections[index]?.status;

          // Priority: frontend state > modification_status > validation_status
          const actualStatus: string = frontendStatus || backendModificationStatus || backendValidationStatus || 'ai_generated';

          const isModified = actualStatus === 'user_modified';
          const isApproved = actualStatus === 'user_approved';
          const isVerified = actualStatus === 'human_verified' || section.preserved === true;
          const isHumanModified = actualStatus === 'human_modified';
          const isAIGenerated = actualStatus === 'ai_generated' && !isVerified;

          // Get current content and ensure it's plain text (strip any HTML tags)
          let currentContent = editedSections[index]?.content || section.content;

          // If content contains HTML tags, strip them to ensure proper rendering through SOPContentRenderer
          if (typeof currentContent === 'string' && (currentContent.includes('<p>') || currentContent.includes('<br>'))) {
            // Strip HTML tags but preserve newlines
            currentContent = currentContent
              .replace(/<br\s*\/?>/gi, '\n')
              .replace(/<\/p>\s*<p>/gi, '\n\n')
              .replace(/<p>/gi, '')
              .replace(/<\/p>/gi, '')
              .replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/g, ' ')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }

          // FIX: Convert emoji-based markers to proper bracket format
          // This handles corrupted data from previous edits where markers lost their brackets
          if (typeof currentContent === 'string') {
            // Convert emoji-prefixed markers (with or without bold formatting) to proper bracket format
            currentContent = currentContent
              // DOCUMENTED marker
              .replace(/📋\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
              .replace(/📋DOCUMENTED:/g, '[DOCUMENTED:')
              // INFERRED marker
              .replace(/🔍\*\*INFERRED:\*\*/g, '[INFERRED:')
              .replace(/🔍INFERRED:/g, '[INFERRED:')
              // ASSUMED marker
              .replace(/💭\*\*ASSUMED:\*\*/g, '[ASSUMED:')
              .replace(/💭ASSUMED:/g, '[ASSUMED:')
              // CONTROL POINT marker
              .replace(/🔍\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
              .replace(/🔍CONTROL POINT:/g, '[CONTROL POINT:')
              // GAP IDENTIFIED marker
              .replace(/⚠️\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
              .replace(/⚠️GAP IDENTIFIED:/g, '[GAP IDENTIFIED:')
              // SCREENSHOT NEEDED marker
              .replace(/📸\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
              .replace(/📸SCREENSHOT NEEDED:/g, '[SCREENSHOT NEEDED:')
              // FLOWCHART NEEDED marker
              .replace(/📊\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
              .replace(/📊FLOWCHART NEEDED:/g, '[FLOWCHART NEEDED:')
              // CONFLICT marker
              .replace(/⚡\*\*CONFLICT:\*\*/g, '[CONFLICT:')
              .replace(/⚡CONFLICT:/g, '[CONFLICT:')
              // SOURCE marker
              .replace(/📄\*\*SOURCE:\*\*/g, '[SOURCE:')
              .replace(/📄SOURCE:/g, '[SOURCE:')
              // ASSUMPTION marker
              .replace(/💡\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:')
              .replace(/💡ASSUMPTION:/g, '[ASSUMPTION:');

            // Remove bold formatting from marker labels: [**DOCUMENTED:**text] -> [DOCUMENTED:text]
            currentContent = currentContent
              .replace(/\[\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
              .replace(/\[\*\*INFERRED:\*\*/g, '[INFERRED:')
              .replace(/\[\*\*ASSUMED:\*\*/g, '[ASSUMED:')
              .replace(/\[\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
              .replace(/\[\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
              .replace(/\[\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
              .replace(/\[\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
              .replace(/\[\*\*CONFLICT:\*\*/g, '[CONFLICT:')
              .replace(/\[\*\*SOURCE:\*\*/g, '[SOURCE:')
              .replace(/\[\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:');

            // Add closing brackets if missing
            // Strategy: Look for [MARKER: that's NOT followed by ] before the next sentence/marker/newline
            // Match patterns:
            // 1. [MARKER:text that goes to end of line
            // 2. [MARKER:text until next marker starts
            // 3. [MARKER:text. (until period + space or newline)
            //
            // BUG FIX: [^\]]+? in the lookahead-based version below can't see past a
            // "]" — so it always assumed a tag was unclosed and force-closed it at the
            // FIRST sentence boundary, even for a legitimate multi-sentence tag that
            // already had its own correctly-placed closing "]" further down (e.g. a
            // Gaps Register entry: "[GAP IDENTIFIED: sentence one. sentence two.]
            // (applies to: ...)"). That produced a stray extra "]" — the real closing
            // bracket became orphaned once this pass inserted its own premature one.
            // Fix: only force-close a tag when it has NO "]" before the next tag
            // keyword or end of string — i.e. it's genuinely unclosed.
            currentContent = currentContent.replace(
              /\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):[^\[]*/g,
              (match: string) => {
                // Already has a closing ] somewhere in this tag span — leave untouched.
                if (match.includes(']')) return match;
                // Genuinely unclosed — close at the first sentence boundary or end.
                const boundaryMatch = match.match(/^([\s\S]*?)(\.\s|\n|$)/);
                if (boundaryMatch) {
                  return `${boundaryMatch[1]}]${boundaryMatch[2]}${match.slice(boundaryMatch[0].length)}`;
                }
                return `${match}]`;
              }
            );
          }
          // PM Step linkage is shown via the section header badge (↔ N PM Steps linked)
          // Inline per-step markers removed per feedback — they clutter the content

          const lastEditedAt = section.last_edited_at;

          // Get status badge styling
          const getStatusBadge = () => {
            if (isApproved) {
              return {
                bg: 'bg-green-100',
                text: 'text-green-700',
                border: 'border-green-200',
                icon: '✅',
                label: 'Approved'
              };
            } else if (isModified) {
              return {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                border: 'border-yellow-200',
                icon: '✏️',
                label: 'Modified'
              };
            } else if (isVerified || isHumanModified) {
              return {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                border: 'border-blue-200',
                icon: '✓',
                label: isVerified ? 'Verified (Preserved)' : 'Human Modified'
              };
            } else {
              return {
                bg: 'bg-purple-100',
                text: 'text-purple-700',
                border: 'border-purple-200',
                icon: '🤖',
                label: 'AI Generated'
              };
            }
          };

          const statusBadge = getStatusBadge();

          return (
            <React.Fragment key={index}>
              <div
                id={`sop-section-${section.section_number || index}`}
                className={`bg-white border-2 rounded-lg p-6 ${
                  isApproved ? 'border-green-300 bg-green-50' :
                  isModified ? 'border-yellow-400 bg-yellow-50' :
                  (isVerified || isHumanModified) ? 'border-blue-300 bg-blue-50' :
                  'border-gray-200'
                }`}
              >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-super-dark-purple flex items-center flex-wrap gap-2">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 shrink-0">
                    {section.section_number || `${index + 1}`}
                  </span>
                  {section.section_title || section.title}
                  {/* PM ↔ SOP linkage badge */}
                  {(() => {
                    const allLinkages: any[] = sopData?.step_linkages || [];
                    const base = (section.section_number || '').replace(/\.0$/, '');
                    // For any section with sub-sections: show count badge (4.0→4.1,4.2... / 5.0→5.1,5.2... etc.)
                    if (base && /^\d+$/.test(base)) {
                      const subPattern = new RegExp(`^${base}\\.\\d+$`);
                      const subLinks = allLinkages.filter(
                        (l: any) => l.sop_section_number && subPattern.test(l.sop_section_number) && (l.confidence || 0) >= 60
                      );
                      if (subLinks.length > 0) {
                        return (
                          <span
                            className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-normal whitespace-nowrap"
                            title={subLinks.map((l: any) => `${l.sop_section_number} ↔ PM Step ${l.process_step_number}: ${l.process_step_title}`).join('\n')}
                          >
                            ↔ {subLinks.length} PM Step{subLinks.length !== 1 ? 's' : ''} linked
                          </span>
                        );
                      }
                    }
                    // Fallback: direct linkage badge (for sub-sections or old single-link format)
                    const linkage = allLinkages.find(
                      (l: any) => l.sop_section_number === section.section_number && (l.confidence || 0) >= 60
                    );
                    if (!linkage) return null;
                    const stepNum = linkage.process_step_number;
                    const stepTitle = linkage.process_step_title;
                    const pmLabel = stepTitle
                      ? `PM Step ${stepNum ? stepNum + ': ' : ''}${stepTitle}`
                      : stepNum ? `PM Step ${stepNum}` : 'PM Step';
                    return (
                      <span
                        className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-normal whitespace-nowrap"
                        title={`Linked to Process Map step: ${pmLabel}`}
                      >
                        ↔ {pmLabel}
                      </span>
                    );
                  })()}
                </h4>
                <div className="flex items-center space-x-2">
                  {/* Status Badge with Icon */}
                  <span
                    className={`text-xs ${statusBadge.bg} ${statusBadge.text} border ${statusBadge.border} px-3 py-1 rounded-full font-medium flex items-center gap-1.5`}
                    title={`Status: ${statusBadge.label}${lastEditedAt ? `\nLast edited: ${new Date(lastEditedAt).toLocaleString()}` : ''}`}
                  >
                    <span>{statusBadge.icon}</span>
                    <span>{statusBadge.label}</span>
                  </span>

                  {/* Timestamp indicator for modified sections */}
                  {(isModified || isApproved) && lastEditedAt && (
                    <span className="text-xs text-gray-500 italic" title={new Date(lastEditedAt).toLocaleString()}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {(() => {
                        const now = new Date();
                        const edited = new Date(lastEditedAt);
                        const diffMinutes = Math.floor((now.getTime() - edited.getTime()) / 60000);
                        if (diffMinutes < 1) return 'Just now';
                        if (diffMinutes < 60) return `${diffMinutes}m ago`;
                        const diffHours = Math.floor(diffMinutes / 60);
                        if (diffHours < 24) return `${diffHours}h ago`;
                        return `${Math.floor(diffHours / 24)}d ago`;
                      })()}
                    </span>
                  )}

                  {!isEditing && (
                    <button
                      onClick={() => handleEditSection(index, section.content, section.section_title || section.title)}
                      className="text-gray-600 hover:text-purple-600 transition-colors p-2"
                      title="Edit section"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Section Title</label>
                    <input
                      type="text"
                      value={editedSectionTitles[index] ?? (section.section_title || section.title || '')}
                      onChange={(e) => setEditedSectionTitles(prev => ({ ...prev, [index]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      placeholder="Section title..."
                    />
                  </div>
                  <RichTextEditor
                    ref={sectionEditorRef}
                    content={currentContent}
                    onChange={(newContent) => handleContentChange(index, newContent)}
                    placeholder="Edit section content..."
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Auto-saves after 30 seconds</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCancelEdit(index)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveSection(index)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Conditional rendering for Key Definitions section
                (section.section_title === 'Key Definitions' || section.title === 'Key Definitions') ? (
                  // If terminology available, show ONLY terminology (hide AI content)
                  terminologyDefinitions.length > 0 ? (
                    <div>
                      <p className="font-semibold mb-2 p-1">
                        From Terminology Analysis ({terminologyDefinitions.length} terms)
                      </p>
                      <table className="w-full text-sm border-collapse">
                        <tbody>
                          {terminologyDefinitions.map((term: any) => (
                            <tr key={term.id} className="group hover:bg-gray-50 border-b border-gray-100 last:border-0">
                              <td className="py-2 pr-4 align-top w-1/4 whitespace-nowrap">
                                <span className="font-semibold text-gray-900">{term.term}</span>
                              </td>
                              <td className="py-2 pr-2 align-top text-gray-700">{term.definition}</td>
                              <td className="py-2 align-top w-8">
                                <button
                                  onClick={() => deleteTerminologyDefinition(term.id)}
                                  className="text-red-500 transition-opacity flex-shrink-0"
                                  title="Remove from SOP"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    // No terminology yet, show AI-generated content with SOPContentRenderer
                    <SOPContentRenderer content={currentContent} viewMode={contentViewMode} showMarkers={showMarkers} sectionTitle={section.section_title || section.title || ''} registryNums={registryNums} />
                  )
                ) : (
                  // For all other sections, always use SOPContentRenderer for proper marker display
                  <SOPContentRenderer content={currentContent} viewMode={contentViewMode} showMarkers={showMarkers} sectionTitle={section.section_title || section.title || ''} registryNums={registryNums} />
                )
              )}
              </div>

              {/* REQ-SOP-2.3: Add Section Button */}
              <div className="flex items-center justify-center my-4">
                <button
                  onClick={() => openAddSectionModal(index)}
                  className="group flex items-center space-x-2 px-4 py-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors border-2 border-dashed border-purple-300 hover:border-purple-400"
                  title="Add custom section here"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Add Section</span>
                </button>
              </div>
            </React.Fragment>
          );
        })}

        {/* Add Section Modal */}
        <AddSectionModal
          isOpen={isAddSectionModalOpen}
          onClose={() => {
            setIsAddSectionModalOpen(false);
            setInsertAfterIndex(null);
          }}
          onAdd={handleAddSection}
          insertAfterSection={insertAfterIndex !== null && sopData?.sections?.[insertAfterIndex]?.section_number}
        />

        {/* Revision History -- number computed per-SOP (registryNums/revisionSectionNumber
            above): a client format like Pacific Life's 7-section structure renders this
            at 7.0, not the generic format's 13.0. */}
        {sopData?.sections && sopData.sections.length > 0 && !sopData.sections.some((s: any) => s.section_number === revisionSectionNumber) && (
          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-center mb-4">
              <h4 className="text-lg font-semibold text-super-dark-purple flex items-center gap-2">
                <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0">
                  {revisionSectionNumber}
                </span>
                Revision History
              </h4>
            </div>
            <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-3 py-2 font-semibold text-gray-600 w-[12%]">Version</th>
                  <th className="px-3 py-2 font-semibold text-gray-600 w-[20%]">Changes By</th>
                  <th className="px-3 py-2 font-semibold text-gray-600 w-[50%]">Summary of Changes</th>
                  <th className="px-3 py-2 font-semibold text-gray-600 w-[18%]">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-200">
                  <td className="px-3 py-2 text-gray-700">1.0</td>
                  <td className="px-3 py-2 text-gray-700">{user?.name || user?.email || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">First edition</td>
                  <td className="px-3 py-2 text-gray-700">{sopData?.effective_date ? new Date(sopData.effective_date).toLocaleDateString() : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* End-to-End Process Flow (shared component) */}
      {/* <EndToEndProcessFlow
        steps={steps}
        onStepsChange={(newSteps) => {
          setSteps(newSteps);
          saveSteps(newSteps);
        }}
        isSaving={isSaving}
        saveQueued={saveQueued}
        onChatOpen={(stepIndex, stepText) => {
          setChatStepIdx(stepIndex);
          setChatOpen(true);
        }}
        projectId={currentProject?.id || synthesis?.project_id}
      /> */}




      {/* Render StepChatModal for AI Q&A about a process step */}
      {chatStepIdx !== null && typeof chatStepIdx === 'number' && chatStepIdx < steps.length && (() => {
        const stepVal = steps[chatStepIdx as number];
        let stepStr = '';
        if (typeof stepVal === 'string') {
          stepStr = stepVal;
        } else if (isStepObj(stepVal)) {
          stepStr = stepVal.step;
        }
        return (
          <EnhancedStepChatModal
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            step={stepStr}
            projectId={synthesis?.project_id ?? undefined}
            stepIndex={chatStepIdx as number}
            onStepsExpanded={() => { }}
            onStepsUpdated={onRefresh}
          />
        );
      })()}
      {/* SOP Type Tabs */}
      {allSOPs.length > 1 && (
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-1">
            {/* Basic SOP Tab */}
            {basicSOPs.length > 0 && (
              <button
                onClick={() => setActiveSOPTab('basic')}
                className={`px-6 py-3 font-medium text-sm transition-all ${
                  activeSOPTab === 'basic'
                    ? 'border-b-2 border-electric-blue text-electric-blue bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                Basic SOP
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {basicSOPs.length}
                </span>
              </button>
            )}

            {/* Enhanced SOP Tab */}
            {enhancedSOPs.length > 0 && (
              <button
                onClick={() => setActiveSOPTab('enhanced')}
                className={`px-6 py-3 font-medium text-sm transition-all ${
                  activeSOPTab === 'enhanced'
                    ? 'border-b-2 border-purple-500 text-purple-600 bg-purple-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                Enhanced SOP
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {enhancedSOPs.length}
                </span>
              </button>
            )}

          </div>
        </div>
      )}

      {showVerificationPanel && sopData?.verification?.metadata && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">SOP Verification Details</h3>
              <button
                onClick={() => setShowVerificationPanel(false)}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <VerificationPanel
                metadata={sopData.verification.metadata}
                embedded
                correctionScope="sop"
                correctionScopeId={sop?.id}
                onMetadataUpdated={() => {
                  if (typeof loadAnalysisResults === 'function') loadAnalysisResults();
                }}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showClearConfirm}
        title="Clear SOP"
        message="This will permanently delete all SOPs for this project. This action cannot be undone."
        confirmText="Yes, Clear SOP"
        cancelText="Cancel"
        variant="danger"
        loading={isClearingSOP}
        onConfirm={clearSOP}
        onCancel={() => setShowClearConfirm(false)}
      />

      <ConfirmDialog
        isOpen={pendingRegenerateType !== null}
        title="Regenerate SOP"
        message={`${terminologyIncomplete ? pendingTermMessage + ' ' : ''}This rebuilds the entire SOP from scratch using the latest stakeholders, process map, and approved terminology — every section is replaced, not merged. Any manual edits made directly in the current SOP will be lost. Continue?`}
        confirmText={pendingRegenerateType === 'enhanced' ? 'Yes, Regenerate Enhanced SOP' : 'Yes, Regenerate SOP'}
        cancelText="Cancel"
        variant="danger"
        loading={isBasicSOPGenerating || isEnhancingInProgress}
        onConfirm={() => {
          const type = pendingRegenerateType;
          setPendingRegenerateType(null);
          if (type === 'enhanced') generateEnhancedSOP();
          else if (type === 'basic') generateBasicSOP();
        }}
        onCancel={() => setPendingRegenerateType(null)}
      />
    </div>
  );
};

export default SOPGenerationTab;